<template>
  <div class="widget w-option-chain-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header :wid="wid" fieldsConfigManager="advanced" printView>
      <c-incremental-search
        slot="left"
        :ric="ric"
        @error="onSymbolSearchError"
        @change-ric="onSetRIC"
        :instrumentTypes="instrumentTypes"
      />
      <slot name="buttons" slot="buttons" />
    </c-widget-header>
    <div v-if="ric" class="widget-body" v-resize:throttle.100="autoSizeColumns">
      <c-loader v-if="isLoading" />
      <c-streaming-error :show="hasStreamingError" />
      <div class="quote-panel" v-show="!isLoading">
        <v-button
          :class="!quotePanelOpenValue ? 'collapsed quote-panel-bar' : 'quote-panel-bar'"
          :aria-expanded="quotePanelOpenValue ? 'true' : 'false'"
          aria-controls="collapse-quote-panel"
          @click="onUpdateQuotePanel"
        >
          <font-awesome-icon class="mr-2" :icon="['fas', `chevron-${quotePanelOpenValue ? 'down' : 'right'}`]" />
          <span v-show="quotePanelOpenValue">{{ i18n('panels.quoteChart') }}</span>

          <div v-if="!quotePanelOpenValue" class="inline-quote">
            <div class="pricing-bar ml-1">
              <span class="price-label">{{ i18n('q._TRDPRC_1', 'globalLabels.fids').short }}</span>
              <span class="ml-1 mr-1">{{ currencyData.symbol }}</span>
              <c-tick-flash :value="$f(underlyingData['q._TRDPRC_1'], underlyingMetadata['q._TRDPRC_1'])" />
              <span class="price-label ml-3">{{ i18n('q._BID', 'globalLabels.fids').short }}</span>
              <span class="ml-1 mr-1">{{
                `${currencyData.symbol} ${$f(underlyingData['q._BID'], underlyingMetadata['q._BID'])}`
              }}</span>
              <span class="price-label ml-3">{{ i18n('q._ASK', 'globalLabels.fids').short }}</span>
              <span class="ml-1 mr-1">{{
                `${currencyData.symbol} ${$f(underlyingData['q._ASK'], underlyingMetadata['q._ASK'])}`
              }}</span>
            </div>
            <div class="earnings-bar d-flex flex-wrap">
              <div>
                <span class="price-label">{{ $t(`white-label.widgets.optionChain.labels.nextEarnings`) }}</span>
                <span>
                  {{ displayedEarningsDate }}
                </span>
                <span class="market-phase" v-if="nextMarketPhase">
                  {{
                    $t(`white-label.widgets.optionChain.marketPhaseMap.${nextMarketPhase}`)
                      .split(' ')
                      .pop()
                  }}
                </span>
              </div>
              <div>
                <span class="price-label ml-3">{{ $t(`white-label.widgets.optionChain.labels.analystAverage`) }}</span>
                <span class="value ml-1" v-if="underlyingData['recomText']" :class="underlyingData['recomText']"
                  >{{ $t(`white-label.widgets.optionChain.labels.${underlyingData['recomText']}`) }}
                </span>
                <span class="value grey-backg" v-else>
                  -
                </span>
              </div>
              <div>
                <span class="price-label ml-3">{{ $t(`white-label.widgets.optionChain.labels.targetPrice`) }}</span>
                <span class="ml-1 mr-3 value" :class="targetPriceClass"
                  >{{ $f(underlyingData['rkd4.TARGETPRICE'], 'float.price') }}
                </span>
              </div>
            </div>

            <c-action-buttons-bar
              :buy="
                e => {
                  e.stopPropagation();
                  buyClicked();
                }
              "
              :sell="
                e => {
                  e.stopPropagation();
                  sellClicked();
                }
              "
              :cover="
                e => {
                  e.stopPropagation();
                  coverClicked();
                }
              "
              :short="
                e => {
                  e.stopPropagation();
                  shortClicked();
                }
              "
              :alert="
                e => {
                  e.stopPropagation();
                  alertClicked();
                }
              "
              :wid="wid"
            />
          </div>
        </v-button>
        <b-collapse :visible="quotePanelOpenValue" id="collapse-quote-panel">
          <div class="quote-panel-content">
            <b-container fluid class="">
              <b-row>
                <b-col class="pr-0 quote-section-col">
                  <c-option-chain-quote
                    :wid="wid"
                    :underlyingData="underlyingData"
                    :displayedEarningsDate="displayedEarningsDate"
                    :nextMarketPhase="nextMarketPhase"
                    :underlyingMetadata="underlyingMetadata"
                    @buy-clicked="buyClicked"
                    @sell-clicked="sellClicked"
                    @cover-clicked="coverClicked"
                    @short-clicked="shortClicked"
                    @alert-clicked="alertClicked"
                  />
                </b-col>
                <b-col>
                  <c-underlying-chart
                    class="mt-2"
                    :timeseries="timeseries"
                    :underlyingData="underlyingData"
                    :chartType="chartTypeValue"
                    :chartTimespan="chartTimespanValue"
                    @chartType-changed="chartTypeChanged"
                    @chartTimespan-changed="chartTimespanChanged"
                  />
                </b-col>
              </b-row>
            </b-container>
          </div>
        </b-collapse>
      </div>

      <div class="controls-bar" v-show="!isLoading">
        <v-form-group class="expiry-type-selection-group">
          <v-dropdown
            variant="form"
            :text="i18n('controls.series.label')"
            :aria-label="i18n('controls.series.label')"
            class="expiry-type-selection"
          >
            <v-dropdown-form>
              <b-form-group :name="i18n('controls.series.label')">
                <v-form-checkbox
                  class="mb-1"
                  :checked="expiryTypes.weekly"
                  @input="onUpdateExpiryTypes('weekly', $event)"
                  :aria-label="i18n('controls.series.weekly')"
                  >{{ i18n('controls.series.weekly') }}</v-form-checkbox
                >
                <v-form-checkbox
                  class="mb-1"
                  :checked="expiryTypes.monthly"
                  @input="onUpdateExpiryTypes('monthly', $event)"
                  :aria-label="i18n('controls.series.monthly')"
                  >{{ i18n('controls.series.monthly') }}</v-form-checkbox
                >
                <v-form-checkbox
                  :checked="expiryTypes.quarterly"
                  @input="onUpdateExpiryTypes('quarterly', $event)"
                  :aria-label="i18n('controls.series.quarterly')"
                  >{{ i18n('controls.series.quarterly') }}</v-form-checkbox
                >
              </b-form-group>
            </v-dropdown-form>
          </v-dropdown>
        </v-form-group>
        <v-form-group :label="i18n('controls.strikes.label')" label-cols="auto">
          <v-dropdown
            variant="form"
            :disabled="!optionsAvailable"
            :text="strikeText[strikesValue] ? strikeText[strikesValue] : strikesValue"
            :aria-label="i18n('controls.strikes.label')"
          >
            <v-dropdown-item
              v-for="option in strikesOptions"
              :key="`strikes-${option.value}`"
              @click="onUpdateStrikes(option.value)"
            >
              <span class="label">{{ option.text }}</span>
            </v-dropdown-item>
            <v-dropdown-form
              aria-label="custom field"
              @focus="onDropdownFormFocus"
              @submit.prevent
              class="custom-strike-input"
            >
              <v-form-input
                type="number"
                :placeholder="i18n('controls.strikes.customStrikeLabel')"
                min="1"
                step="1"
                :formatter="customStrikesFormatter"
                v-model="customStrikesValue"
                @keydown.enter="onStrikeFormSubmit"
              ></v-form-input>
            </v-dropdown-form>
          </v-dropdown>
        </v-form-group>
        <v-form-radio-group
          name="orderTypeSelect"
          class="form-group"
          buttons
          button-variant="primary"
          :checked="orderTypeValue"
          @input="onUpdateOrderType"
        >
          <v-form-radio value="slo">{{ i18n('controls.orderType.slo') }}</v-form-radio>
          <v-form-radio value="mlo"
            >{{ i18n('controls.orderType.mlo') }}
            <span class="sr-only">{{ i18n('controls.orderType.screenReaderText') }}</span></v-form-radio
          >
        </v-form-radio-group>
        <v-form-group :label="i18n('templates.label')" label-cols="auto">
          <v-form-select
            :disabled="!optionsAvailable"
            :options="fieldsConfigNameOptions"
            v-model="controlFieldsConfigName"
            :aria-label="i18n('templates.label')"
          />
        </v-form-group>
        <v-form-radio-group
          name="viewSelect"
          class="form-group"
          buttons
          button-variant="primary"
          :checked="viewValue"
          @input="onUpdateView"
          ><v-form-radio :value="0">{{ i18n('controls.view.optionChain') }}</v-form-radio>
          <v-form-radio :value="1"
            >{{ i18n('controls.view.optionChainGraph')
            }}<span class="sr-only">{{ i18n('controls.view.sameContent') }}</span></v-form-radio
          >
        </v-form-radio-group>
      </div>

      <div class="analysis-container" v-show="!isLoading">
        <div
          v-if="(controlFieldsConfigName === 'Theoretical' || _tableFids.includes('_THEO_PRC')) && viewValue === 0"
          class="theoretical-calculation-panel pt-2 pb-2 pl-3"
        >
          <span class="mr-4">{{ i18n('controls.calculation.label') }}</span>

          <v-form-checkbox class="mr-3" :checked="streamingActiveValue" @input="onUpdateStreamingActive">{{
            i18n('controls.calculation.streaming')
          }}</v-form-checkbox>

          <v-form-group :label="i18n('controls.calculation.stockPrice')" label-cols="auto" class="mr-1 mr-3">
            <v-form-number
              type="price"
              class="custom-input-number"
              autocomplete="off"
              min="0"
              step=".01"
              :disabled="streamingActiveValue"
              :value="streamingActiveValue ? '0.00' : calculationParameters.stockPrice"
              @input="onInputStockPrice"
              :aria-label="i18n('controls.calculation.stockPrice')"
            />
          </v-form-group>

          <v-form-group :label="i18n('controls.calculation.impVolAdjust')" label-cols="auto" class="mr-1 mr-3">
            <v-form-input
              type="number"
              class="custom-input-number"
              autocomplete="off"
              step=".01"
              :disabled="streamingActiveValue"
              :value="streamingActiveValue ? '0.00' : calculationParameters.impVola"
              @change="onInputImpVolatility"
              :aria-label="i18n('controls.calculation.impVolAdjust')"
            />
          </v-form-group>

          <v-form-group :label="i18n('controls.calculation.target')" label-cols="auto">
            <v-form-datepicker
              value-as-date
              :value="streamingActiveValue ? new Date() : calculationParameters.targetDate"
              :disabled="streamingActiveValue"
              :locale="$i18n.locale"
              :labelHelp="$t('white-label.components.datepicker.labelHelp')"
              :labelPrevMonth="$t('white-label.components.datepicker.labelPrevMonth')"
              :labelCurrentMonth="$t('white-label.components.datepicker.labelCurrentMonth')"
              :labelNextMonth="$t('white-label.components.datepicker.labelNextMonth')"
              :labelPrevYear="$t('white-label.components.datepicker.labelPrevYear')"
              :labelCurrentYear="$t('white-label.components.datepicker.labelCurrentYear')"
              :labelNextYear="$t('white-label.components.datepicker.labelNextYear')"
              @input="onInputTargetDate"
            />
          </v-form-group>

          <v-button
            class="ml-5 mr-2"
            variant="outline-primary"
            name="calculation-apply-button"
            @click="onApplyCalculation"
            :disabled="streamingActiveValue || isCalculationApplied"
          >
            {{ i18n('controls.calculation.apply') }}
          </v-button>

          <v-button
            variant="outline-primary"
            name="calculation-reset-button"
            @click="onResetCalculation"
            :disabled="streamingActiveValue"
          >
            {{ i18n('controls.calculation.reset') }}
          </v-button>
        </div>

        <b-tabs :value="viewValue">
          <b-tab title="">
            <div v-show="orderTypeValue === 'mlo'" class="multi-leg-order-container">
              <!-- only visible if multi-leg-order selected in orderType above -->
              <div class="analysis-panel">
                <v-button
                  :class="analysisPanelOpenValue ? 'collapsed analysis-panel-bar' : 'analysis-panel-bar'"
                  :aria-expanded="analysisPanelOpenValue ? 'true' : 'false'"
                  aria-controls="collapse-analysis-panel"
                  @click="onUpdateAnalysisPanel"
                >
                  <div class="d-flex align-items-center">
                    <font-awesome-icon
                      class="mr-2"
                      :icon="['fas', `chevron-${analysisPanelOpenValue ? 'down' : 'right'}`]"
                    />
                    <c-strategy :legs="legs" @updateStrategy="onUpdateStrategy" />
                  </div>
                </v-button>
                <b-collapse :visible="analysisPanelOpenValue" id="collapse-analysis-panel">
                  <div class="analysis-panel-content">
                    <b-container fluid>
                      <b-row>
                        <b-col>
                          <c-pay-off-chart
                            :legs="legs"
                            :underlyingData="underlyingData"
                            :interestRateData="interestRateData"
                          />
                        </b-col>
                        <b-col>
                          <b-row>
                            <b-col>
                              <c-key-metrics
                                :legs="legs"
                                :underlyingData="underlyingData"
                                :interestRateData="interestRateData"
                                :currencyData="currencyData"
                                :netDebit="netDebit"
                              />
                            </b-col>
                            <b-col> <c-greeks :legs="legs" /> </b-col>
                          </b-row>
                        </b-col>
                      </b-row>
                    </b-container>
                  </div>
                </b-collapse>
              </div>
              <div class="order-panel">
                <b-container fluid>
                  <b-row>
                    <b-col>
                      <div class="pt-3">
                        <c-leg
                          :leg="legs.first"
                          :step="legs.first ? legs.first.step : 1"
                          @leg-deleted="() => deleteLeg('first')"
                          @action-changed="action => updateLegAction('first', action)"
                          @quantity-changed="quantity => updateLegQuantity('first', quantity)"
                        />
                        <c-leg
                          :leg="legs.second"
                          :step="legs.second ? legs.second.step : 1"
                          @leg-deleted="() => deleteLeg('second')"
                          @action-changed="action => updateLegAction('second', action)"
                          @quantity-changed="quantity => updateLegQuantity('second', quantity)"
                        />
                      </div>
                    </b-col>
                    <b-col class="col-5 pt-2">
                      <div class="price-range-container">
                        <div class="price-range-wrapper">
                          <div class="out-of-range-indicator">
                            <span v-show="!marketActive && orderPriceOutOfRangeMid">
                              <font-awesome-icon :icon="['fas', 'caret-left']" />
                            </span>
                          </div>
                          <div class="mid-wrapper mr-3">
                            <span>{{ i18n('order.mid') }}</span>
                            <span>{{ orderPriceRangeMidValue ? `${getAbsPrice(orderPriceRangeMidValue)}` : `-` }}</span>
                          </div>
                          <v-form-input
                            type="range"
                            class="price-range-slider"
                            name=""
                            :disabled="orderPriceRangeValue === null || marketActive"
                            v-model="orderPriceRangeValue"
                            @input="onInputOrderPriceRange"
                            :min="orderPriceRangeMidValue"
                            :max="orderPriceRangeNatValue"
                            step=".01"
                          />
                          <div class="nat-wrapper ml-3">
                            <span>{{ i18n('order.nat') }}</span>
                            <span>{{ orderPriceRangeNatValue ? `${getAbsPrice(orderPriceRangeNatValue)}` : `-` }}</span>
                          </div>
                          <div class="out-of-range-indicator">
                            <span v-show="!marketActive && orderPriceOutOfRangeNat">
                              <font-awesome-icon :icon="['fas', 'caret-right']" />
                            </span>
                          </div>
                        </div>
                        <div class="price-controls-wrapper mt-2">
                          <div class="price-lock-wrapper">
                            <v-form-group
                              :label="i18n('order.price')"
                              label-cols="auto"
                              class="order-price-input-wrapper mr-2"
                            >
                              <v-form-number
                                type="price"
                                :min="0"
                                :step="0.01"
                                :class="{ 'price-range-value': true, [orderPriceRangeSign]: !marketActive }"
                                :value="+priceInputValue || ''"
                                :disabled="orderPriceRangeValue === null || marketActive"
                                @input="onChangeOrderPriceInput"
                              />
                            </v-form-group>
                            <v-button
                              class="lock-price-button mr-2"
                              variant="outline-primary"
                              :disabled="orderPriceRangeValue === null || marketActive"
                              name="lock-price-button"
                              @click="onLockPriceClicked"
                            >
                              <font-awesome-icon :icon="['fas', `lock${orderPriceLocked ? '' : '-open'}`]" />
                            </v-button>
                            <v-button
                              :class="`price-sign ${!marketActive ? orderPriceRangeSign : ''}`"
                              variant="outline-primary"
                              :disabled="orderPriceRangeValue === null || marketActive || orderPriceRangeSign === 'EV'"
                              name="price-sign-button"
                              @click="onOrderPriceRangeSignClicked"
                            >
                              {{ orderPriceRangeSign }}
                            </v-button>
                          </div>
                          <div class="mkt-wrapper ml-2">
                            <v-form-checkbox
                              :disabled="!(legs.first && legs.second)"
                              :checked="marketActive"
                              @input="onInputMarketActive"
                              >{{ i18n('order.market') }}</v-form-checkbox
                            >
                          </div>
                          <v-button
                            :variant="`outline-${orderPriceRangeButton} btn-${orderPriceRangeSign}`"
                            class="ml-2"
                            name="create-order-button"
                            :disabled="
                              !isSymbolTradeable ||
                                !(legs.first && legs.second) ||
                                (orderPriceRangeSign === 'EV' && !marketActive)
                            "
                            @mouseover.once="preloadOrderEntry"
                            @click="onCreateOrderClicked"
                            >{{ i18n('order.createOrder') }}</v-button
                          >
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                </b-container>
              </div>
            </div>
          </b-tab>
          <b-tab title="">
            <div class="option-chain-graph-container">
              <div class="graph-toolbar">
                <v-form-group :label="i18n('graph.controls.view')" label-cols="auto">
                  <v-form-select
                    :value="graphViewValue"
                    :options="graphViewOptions"
                    @input="onUpdateGraphView"
                    :aria-label="i18n('graph.controls.view')"
                  />
                </v-form-group>
                <v-form-group :label="i18n('graph.controls.expiry')" label-cols="auto">
                  <v-form-tags
                    multicolor
                    :placeholder="i18n('graph.controls.placeholder')"
                    :value="expiries"
                    :maxTags="40"
                    :autocompleteItems="expiriesComplete"
                    :autocompleteMinLength="0"
                    :add-only-from-autocomplete="true"
                    :isDuplicate="
                      (tags, tag) => {
                        return tags.map(t => t.id).indexOf(tag.id) !== -1;
                      }
                    "
                    @input="onUpdateExpiries"
                    @inputText="expiryInput = $event"
                  />
                </v-form-group>
              </div>

              <b-container fluid class="graph-content">
                <b-row class="chart-toolbar-container">
                  <b-col v-show="graphViewValue === 'all' || graphViewValue === 'calls'">
                    <div class="chart-toolbar mr-1">
                      <v-form-group :label="i18n('graph.controls.call')" label-cols="auto">
                        <v-form-select
                          :value="graphFidTopValue"
                          :options="graphFidOptions"
                          @input="onUpdateGraphFidTop"
                          :aria-label="i18n('graph.controls.call')"
                        />
                      </v-form-group>
                    </div>
                    <div class="chart-panel chart-calls-top">
                      <c-option-chart
                        :chartData="chartTopC"
                        :chartType="graphChartTopType"
                        ref="chartTopC"
                        :graphName="i18n('graph.controls.call') + getGraphFidText(graphFidTopValue)"
                        aria-hidden="true"
                      />
                    </div>
                  </b-col>
                  <b-col v-show="graphViewValue === 'all' || graphViewValue === 'puts'">
                    <div class="chart-toolbar">
                      <span class="mr-2">{{ i18n('graph.controls.put') }}</span>
                      <span>{{ getGraphFidText(graphFidTopValue) }}</span>
                    </div>
                    <div class="chart-panel chart-puts-top">
                      <c-option-chart
                        :chartData="chartTopP"
                        :chartType="graphChartTopType"
                        ref="chartTopP"
                        :graphName="i18n('graph.controls.put') + getGraphFidText(graphFidTopValue)"
                        aria-hidden="true"
                      />
                    </div>
                  </b-col>
                </b-row>
                <b-row class="chart-toolbar-container">
                  <b-col v-show="graphViewValue === 'all' || graphViewValue === 'calls'">
                    <div class="chart-toolbar mr-1">
                      <v-form-group :label="i18n('graph.controls.call')" label-cols="auto">
                        <v-form-select
                          :value="graphFidBottomValue"
                          :options="graphFidOptions"
                          @input="onUpdateGraphFidBottom"
                          :aria-label="i18n('graph.controls.call')"
                        />
                      </v-form-group>
                    </div>
                    <div class="chart-panel chart-calls-bottom">
                      <c-option-chart
                        :chartData="chartBottomC"
                        :chartType="graphChartBottomType"
                        ref="chartBottomC"
                        :graphName="i18n('graph.controls.call') + getGraphFidText(graphFidBottomValue)"
                        aria-hidden="true"
                      />
                    </div>
                  </b-col>
                  <b-col v-show="graphViewValue === 'all' || graphViewValue === 'puts'">
                    <div class="chart-toolbar">
                      <span class="mr-2">{{ i18n('graph.controls.put') }}</span>
                      <span>{{ getGraphFidText(graphFidBottomValue) }}</span>
                    </div>
                    <div class="chart-panel chart-puts-bottom">
                      <c-option-chart
                        :chartData="chartBottomP"
                        :chartType="graphChartBottomType"
                        ref="chartBottomP"
                        :graphName="i18n('graph.controls.put') + getGraphFidText(graphFidBottomValue)"
                        aria-hidden="true"
                      />
                    </div>
                  </b-col>
                </b-row>
              </b-container>
            </div>
          </b-tab>
        </b-tabs>
      </div>

      <ag-grid-vue
        v-show="viewValue !== 1 && !isLoading"
        class="c-data-grid ag-theme-balham-dark"
        animateRows
        :asyncTransactionWaitMillis="gridUpdateInterval"
        suppressContextMenu
        @grid-ready="onGridReady"
        :columnDefs="columns"
        :components="renderers"
        :defaultColDef="defaultColumnDef"
        ensureDomOrder
        immutableData
        :gridOptions="gridOptions"
        :groupDisplayType="'groupRows'"
        :rowHeight="agGridRowHeight"
        suppressFieldDotNotation
        :suppressColumnVirtualisation="isAccessibilityEnabled"
        @cellClicked="onCellClicked"
        @cellKeyDown="onCellKeydown"
        @rowGroupOpened="onRowGroupOpened"
      />
    </div>
    <v-alert v-else align="center" class="m-2" show variant="light">{{ i18n('nosymbol', 'globalMessages') }}</v-alert>
    <b-toaster :name="notifierId"></b-toaster>
  </div>
</template>

<script>
import CLoader from '@custom/CLoader';
import CStreamingError from '@custom/CStreamingError';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetHeaderButton from '@custom/widgetContainer/CWidgetHeaderButton';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CIncrementalSearch from '@custom/CIncrementalSearch';
import streamableFidsFilter from '@/utils/streamableFids';

import resize from 'vue-resize-directive';

import CPriceEntry from '@custom/CPriceEntry';
import CTickFlash from '@custom/CTickFlash';
import COptionChainQuote from '@custom/COptionChainQuote';
import CActionButtonsBar from '@custom/CActionButtonsBar';
import CUnderlyingChart from '@custom/CUnderlyingChart';
import CStrategy from '@custom/CStrategy';
import CPayOffChart from '@custom/CPayOffChart';
import CKeyMetrics from '@custom/CKeyMetrics';
import CGreeks from '@custom/CGreeks';
import CLeg from '@custom/CLeg';
import COptionChart from '@custom/COptionChart';

import { API } from '@/state/api';
import { AgGridVue } from 'ag-grid-vue';
import { change as ChangeRulesPack } from '@custom/dataGrid/classRules';
import { TickFlashRenderer, OptionTickFlashRenderer, JSTickFlashRenderer } from '@custom/dataGrid/renderers';

import { capitalize } from 'lodash-es';

import moment from 'moment-timezone';
import { toTimezoneDate } from '@/utils/date';

import { Greeks, Probability, Volatility, Price } from '@/utils/optionChainServices/calculationService';

import { isBuy as isLegBuy, isSell as isLegSell } from '@/utils/optionChainServices/legService';

import { OptionLeg, StockLeg, getLegAction, isOption } from '@/utils/optionChainServices/legService';

import { getTimeToMaturityInYears } from '@/utils/optionChainServices/helpers/date';

import { library as fontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { faMinus as fasMinus } from '@fortawesome/pro-regular-svg-icons/faMinus';
import { faPlus as fasPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faChevronDown as fasChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronRight as fasChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faCaretLeft as fasCaretLeft } from '@fortawesome/pro-regular-svg-icons/faCaretLeft';
import { faCaretRight as fasCaretRight } from '@fortawesome/pro-regular-svg-icons/faCaretRight';
import { faLock as fasLock } from '@fortawesome/pro-regular-svg-icons/faLock';
import { faLockOpen as fasLockOpen } from '@fortawesome/pro-regular-svg-icons/faLockOpen';
import optionChainHeaderRenderer from './optionChainHeaderRenderer';
import optionChainGroupHeaderRenderer from './optionChainGroupHeaderRenderer';
import { getCurrencyData, getInterestRate } from './interestRate';

fontAwesomeLibrary.add(
  fasMinus,
  fasPlus,
  fasChevronDown,
  fasChevronRight,
  fasCaretLeft,
  fasCaretRight,
  fasLock,
  fasLockOpen
);

/*
import {
  calculateMiddlePriceForOptionLegs,
  calculateNaturalPriceForOptionLegs,
  calculateMiddlePriceForStockOptionLegs,
  calculateNaturalPriceForStockOptionLegs
} from '@/utils/calculations/price';
*/

import widgetCommons from '@mixins/widgetCommons';
import gridNavigation from '@mixins/gridNavigation';
import styleVars from '@mm5-components/scss/variables.scss';

const i18nNamespace = 'white-label.widgets.optionChain';

export default {
  name: 'w-option-chain-widget',
  mixins: [widgetCommons, gridNavigation],
  directives: {
    resize
  },
  components: {
    CLoader,
    CStreamingError,
    CWidgetHeader,
    CWidgetHeaderButton,
    CWidgetControlBar,
    CIncrementalSearch,
    CPriceEntry,
    AgGridVue,
    CTickFlash,
    TickFlashRenderer,
    OptionTickFlashRenderer,
    JSTickFlashRenderer,
    COptionChainQuote,
    CActionButtonsBar,
    CUnderlyingChart,
    CStrategy,
    CPayOffChart,
    CKeyMetrics,
    CGreeks,
    CLeg,
    COptionChart,
    optionChainHeaderRenderer,
    optionChainGroupHeaderRenderer
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      calculationParameters: {
        stockPrice: '0.00',
        impVola: '0.00',
        targetDate: new Date()
      },
      countries: 'US,CA',
      exchanges: 'TOR,CVE,NLB,CNX,MOE,ALP,NYQ,NYS,NAQ,NAS,NSQ,NSM,NMQ,NMS,ASQ,BTQ,ASE,PCQ,PSE,ADC,NYM,OPQ,MXP,IOM,PNK',
      instrumentTypes: ['STO', 'IND', 'FUN', 'CER'],
      isLoading: false,
      hasError: false,
      hasStreamingError: false,
      optionsAvailable: false,
      optionChainInitialized: false,
      selectedTemplateConfig: null,
      tableFids: null,
      mandatoryFids: ['q.RIC', 'q._VOLA_ASK', 'q._VOLA_BID'],
      streamingRics: [],
      symbolSubscriptionId: null,
      optionsSubscriptionId: null,
      optionsSnapshots: [],
      formats: {},
      optionsStreamingFidsMapping: {
        C_TRDPRC_1: 'q._TRDPRC_1',
        P_TRDPRC_1: 'q._TRDPRC_1',
        C_NETCHNG_1: 'q._NETCHNG_1',
        P_NETCHNG_1: 'q._NETCHNG_1',
        C_ACVOL_1: 'q._ACVOL_1',
        P_ACVOL_1: 'q._ACVOL_1',
        C_VOLA_BID: 'q._VOLA_BID',
        P_VOLA_BID: 'q._VOLA_BID',
        C_BID: 'q._BID',
        P_BID: 'q._BID',
        C_ASK: 'q._ASK',
        P_ASK: 'q._ASK',
        C_VOLA_ASK: 'q._VOLA_ASK',
        P_VOLA_ASK: 'q._VOLA_ASK',
        C_OPINT_1: 'q.OPINT_1',
        P_OPINT_1: 'q.OPINT_1',
        C_BIDSIZE: 'q.BIDSIZE',
        P_BIDSIZE: 'q.BIDSIZE',
        C_ASKSIZE: 'q.ASKSIZE',
        P_ASKSIZE: 'q.ASKSIZE',
        C_HIGH_1: 'q._HIGH_1',
        P_HIGH_1: 'q._HIGH_1',
        C_LOW_1: 'q._LOW_1',
        P_LOW_1: 'q._LOW_1',
        C_HST_CLOSE: 'q._HST_CLOSE',
        P_HST_CLOSE: 'q._HST_CLOSE'
      },
      optionsStreamingFids: {
        'q.RIC': { formatter: 'string' },
        'q._TRDPRC_1': { formatter: 'float.price' },
        'q._NETCHNG_1': { formatter: 'float.price.change' },
        'q._ACVOL_1': { formatter: 'int' },
        'q._VOLA_BID': { formatter: 'float.price' },
        'q._BID': { formatter: 'float.price' },
        'q._ASK': { formatter: 'float.price' },
        'q._VOLA_ASK': { formatter: 'float.price' },
        'q.OPINT_1': { formatter: 'int' },
        'q.BIDSIZE': { formatter: 'int' },
        'q.ASKSIZE': { formatter: 'int' },
        'q._HIGH_1': { formatter: 'float.price' },
        'q._LOW_1': { formatter: 'float.price' },
        'q._HST_CLOSE': { formatter: 'float.price' },
        'q.DELTA': { formatter: 'float.price' },
        'q.GAMMA': { formatter: 'float.price' },
        'q.THETA': { formatter: 'float.price' },
        'q.RHO': { formatter: 'float.price' },
        'q.VEGA': { formatter: 'float.price' }
        //'q._IMP_VOLT': { formatter: 'float' }
      },
      optionsFids: {
        'xopt._EXCHG_NAME': { formatter: null },
        'xopt.EXPIR_CYCL': { formatter: null },
        'xopt._MONTH': { formatter: null },
        'xopt._STRIKE': { formatter: 'float.price' },
        'xopt._MATURITY_ISO': { formatter: null },
        'xopt._TYPE_CP': { formatter: null },
        'xopt.LOT_SIZE_A': { formatter: null },
        'x.RIC': { formatter: null },
        //'name._SHORTNAME': { formatter: null },
        'xopt._DSPLY_NAME': { formatter: null },
        'q._TYPE': { formatter: null }
      },
      greeksFids: {
        C_DELTA: { formatter: 'float' },
        P_DELTA: { formatter: 'float' },
        C_GAMMA: { formatter: 'float' },
        P_GAMMA: { formatter: 'float' },
        C_THETA: { formatter: 'float' },
        P_THETA: { formatter: 'float' },
        C_RHO: { formatter: 'float' },
        P_RHO: { formatter: 'float' },
        C_VEGA: { formatter: 'float' },
        P_VEGA: { formatter: 'float' }
      },
      intrinsicExtrinsicFids: {
        C_INTRINSIC: { formatter: 'float.price' },
        P_INTRINSIC: { formatter: 'float.price' },
        C_EXTRINSIC: { formatter: 'float.price' },
        P_EXTRINSIC: { formatter: 'float.price' }
      },
      probabilityFids: {
        C_PROB_ITM: { formatter: 'float.price' },
        P_PROB_ITM: { formatter: 'float.price' },
        C_PROB_OTM: { formatter: 'float.price' },
        P_PROB_OTM: { formatter: 'float.price' },
        C_PROB_TOUCH: { formatter: 'float.price' },
        P_PROB_TOUCH: { formatter: 'float.price' }
      },
      theoreticalFids: {
        C_THEO_PRC: { formatter: 'float' },
        P_THEO_PRC: { formatter: 'float' },
        C_IMP_VOL: { formatter: 'float' },
        P_IMP_VOL: { formatter: 'float' }
      },
      tickFashCells: ['_BID', '_ASK'],
      classRulesCells: {
        _NETCHNG_1: ChangeRulesPack,
        _BID: {
          'column-buy': () => {
            return this.getBuySellClasses('bid') === 'column-buy';
          },
          'column-sell': () => {
            return this.getBuySellClasses('bid') === 'column-sell';
          }
        },
        _ASK: {
          'column-buy': () => {
            return this.getBuySellClasses('ask') === 'column-buy';
          },
          'column-sell': () => {
            return this.getBuySellClasses('ask') === 'column-sell';
          }
        }
      },
      columns: null,
      gridOptions: {
        //suppressCellSelection: true,
        getRowNodeId: data => data.ROW_ID,
        groupRowRendererParams: {
          innerRenderer: params => this.processRowGroupTitle(params.node.key), // Handles row group description text in ag-grid
          suppressCount: true // turn off the row count,
        },
        localeText: { noRowsToShow: 'No options available' },

        columnTypes: { actionable: {} },
        navigateToNextHeader: this.navigateToNextHeaderWidget,
        navigateToNextCell: this.navigateToNextCellWidget,
        tabToNextHeader: this.tabToNextHeaderWidget,
        tabToNextCell: this.tabToNextCell
      },
      defaultColumnDef: {
        width: 40,
        valueFormatter: this.fidletValueFormatter,
        suppressMenu: true,
        suppressMovable: true,
        headerClass: 'text-center',
        headerComponentFramework: optionChainHeaderRenderer,
        headerComponentParams: {
          srLabel: this.$t(`${i18nNamespace}.table.header.strike`)
        },
        cellClass: ['text-right'],
        cellClassRules: {
          'row-indicator': params => {
            const colID = params.colDef.field;
            if (colID === 'C_THEO_PRC' || colID === 'P_THEO_PRC') return false;

            const prefix = colID ? colID.charAt(0) : '';
            if (prefix === 'C' && +params.data['C_STRIKE'] < +this.underlyingData['q._TRDPRC_1']) {
              return true;
            }
            if (prefix === 'P' && +params.data['P_STRIKE'] >= +this.underlyingData['q._TRDPRC_1']) {
              return true;
            }
            return false;
          }
        },
        cellRendererParams: () => {
          return { state: this.$store.state };
        }
        // headerComponentParams: {
        //   menuIcon: 'fa-bars',
        //   template: this.getHeaderTemplate()
        // },
      },
      // Main data
      underlyingData: {},
      nextEarningsDate: '',
      nextMarketPhase: '',
      underlyingMetadata: {},
      orderPriceData: {},
      currencyData: {},
      interestRateData: {},
      options: null,
      groupModel: null,
      legs: {
        first: null,
        second: null
      },
      currentStrategy: null,
      // Timeseries data for underlyingChart
      timeseries: {
        intradayData: null,
        historicalData: null
      },
      // Price range (order)
      orderPriceRangeSign: 'EV',
      orderPriceRangeButton: 'primary',
      orderPriceOutOfRangeMid: false,
      orderPriceOutOfRangeNat: false,
      orderPriceRangeValue: null,
      priceInputValue: 0,
      orderPriceRangeMidValue: null,
      orderPriceRangeNatValue: null,
      orderPriceLocked: false,
      marketActive: false,
      // Option Chain Graph
      expiryInput: '',
      allExpiries: [],
      defaultExpiriesCount: 5,
      graphInitialized: false,
      chartTopC: {},
      chartTopP: {},
      chartBottomC: {},
      chartBottomP: {},
      colorMap: {},
      focusedGroupIndex: null,
      customStrikesValue: 8
    };
  },
  watch: {
    ric: {
      handler() {
        this.resetOptionChain();
        this.fetch();
      }
    },
    linkedRic: {
      handler(ric) {
        if (!ric || ric === this.ric) return;
        API.Rest.get(
          `/product/td/quicksearch?text=${encodeURIComponent(ric)}&exchanges=${this.exchanges}&countries=${
            this.countries
          }&instrumentTypes=${this.instrumentTypes}`
        ).then(result => {
          if (result.data.data.length > 0) {
            this.$store.commit(`widget/${this.wid}/RIC_CHANGED`, ric);
          }
        });
      },
      immediate: true
    },
    legs: {
      handler: 'handleLegsChanged',
      deep: true
    },
    orderPriceRangeValue: {
      handler: 'handleOrderPriceRangeValue',
      immediate: false
    },
    locale() {
      this.updateColumns(); // set new column headers
      this.gridOptions.api.redrawRows(); // redraws rows to update to new price formatting
    },
    timezone() {
      this.gridOptions.api.redrawRows(); // redraws rows to update the "days to expiry"
    },
    fontSize() {
      this.$nextTick(() => {
        this.setTableRowsHeight();
      });
    },
    buySellAtType() {
      if (this.gridOptions && this.gridOptions.api) {
        this.gridOptions.api.refreshCells();
      }
    },
    controlFieldsConfigName: {
      handler(val) {
        if (val) this.selectedTemplateConfig = this.relatedTemplates.find(fc => fc.name === val);
      },
      immediate: true
    },
    _tableFids: {
      handler(val) {
        //Bid/Ask columns should be close to the strike price
        val.splice(val.indexOf('q._BID'), val.indexOf('q._BID') > -1 ? 2 : 0);
        const callFids = [...val, 'q._BID', 'q._ASK'],
          putFids = ['q._BID', 'q._ASK', ...val];
        this.tableFids = {
          C: callFids,
          P: putFids
        };
        this.onUpdateTemplate();
      },
      immediate: true
    }
    /* enable to support widget synchronization during delayed updates
    reflushFlag(newValue) {
      if (!newValue.includes(this.wid)) {
        this.gridOptions.api.flushAsyncTransactions();
      }
    }
    */
  },
  computed: {
    //selected strike text
    strikeText() {
      return {
        4: '4',
        8: '8',
        12: '12',
        25: '25',
        all: this.i18n('controls.strikes.options.all')
      };
    },
    // Option-Chain
    ric() {
      return this.$store.state.widget[this.wid].ric;
    },
    linkedRic() {
      return this.$store.state.widget[this.wid].linkedRic;
    },
    expectedOptionsSnapshots() {
      return this.streamingRics; // wirklich alle?
    },
    targetPriceClass() {
      if (this.underlyingData['rkd4.TARGETPRICE'] === '-' || this.underlyingData['rkd4.TARGETPRICE'] === '') {
        return 'grey-backg';
      }
      return this.underlyingData['q._TRDPRC_1'] < this.underlyingData['rkd4.TARGETPRICE'] ? 'positive' : 'negative';
    },
    isUSSymbol() {
      return this.currencyData.country === 'US';
    },
    isSymbolTradeable() {
      // TODO: get values 'isMultilegTrading' and 'isMultilegForCanadianOptions' from (TD) config
      // return isMultilegTrading && (this.isUSSymbol || config.isMultilegForCanadianOptions)
      return true;
    },
    buySellAtType() {
      return this.$store.state.applicationSettings.orders.settings.options.buySellAtType;
    },
    quotePanelOpenValue() {
      return this.$store.state.widget[this.wid].quotePanelOpen;
    },
    chartTypeValue() {
      return this.$store.state.widget[this.wid].chartType;
    },
    chartTimespanValue() {
      return this.$store.state.widget[this.wid].chartTimespan;
    },
    expiryTypes() {
      return this.$store.state.widget[this.wid].expiryTypes;
    },
    strikesValue: {
      get() {
        return this.$store.state.widget[this.wid].strikes;
      },
      set(strikes) {
        this.$store.commit(`widget/${this.wid}/STRIKES_CHANGED`, strikes);
      }
    },
    strikesOptions() {
      return [
        { value: '4', text: '4' },
        { value: '8', text: '8' },
        { value: '12', text: '12' },
        { value: '25', text: '25' },
        { value: 'all', text: this.i18n('controls.strikes.options.all') }
      ];
    },
    orderTypeValue() {
      return this.$store.state.widget[this.wid].orderType;
    },
    /* enable to support widget synchronization during delayed updates
    reflushFlag() {
      return this.$store.state.widget.reflushFlag;
    },
    */
    relatedFieldsConfig() {
      return this.$store.state.fieldsConfig.widgets[this.factoryName] || {};
    },
    relatedTemplates() {
      return this.relatedFieldsConfig ? this.relatedFieldsConfig.templates : [];
    },
    fieldsConfigNameOptions() {
      return this.relatedTemplates.map(template => ({
        value: template.name,
        text: template.custom ? template.name : this.$t('white-label.templates.' + template.name)
      }));
    },
    controlFieldsConfigName: {
      get() {
        const nameInStore = this.widgetStore.template;
        //With the template manager support, the template name case is modified, below code is to handle that
        if (nameInStore) {
          const template = this.relatedTemplates.find(
            template => template.name.toLowerCase() === nameInStore.toLowerCase()
          );
          if (template) return template.name;
        }
        return this.relatedFieldsConfig ? this.relatedFieldsConfig.defaultTemplate : '';
      },
      set(name) {
        // don't set the template if it's already set from an external component
        if (name === this.controlFieldsConfigName) return;
        this.$store.commit(`widget/${this.wid}/TEMPLATE_SET`, name);
      }
    },
    _tableFids() {
      if (!this.selectedTemplateConfig) return [];
      return this.relatedFieldsConfig.pinnedFids.concat(
        this.relatedFieldsConfig.requiredFids.filter(
          fid => !this.relatedFieldsConfig.pinnedFids.includes(fid) && !this.selectedTemplateConfig.fids.includes(fid)
        ),
        this.selectedTemplateConfig.fids.filter(fid => !this.relatedFieldsConfig.pinnedFids.includes(fid))
      );
    },
    viewValue() {
      return this.$store.state.widget[this.wid].view;
    },
    analysisPanelOpenValue() {
      return this.$store.state.widget[this.wid].analysisPanelOpen;
    },
    legsValue() {
      return this.$store.state.widget[this.wid].legs;
    },
    netDebit() {
      if (!this.orderPriceLocked) {
        return null;
      } else {
        return this.orderPriceRangeValue * (this.orderPriceRangeSign === 'DR' ? -1 : 1);
      }
    },
    timezone() {
      return this.$store.state.applicationSettings.general.settings.timezone;
    },
    // AG-Grid
    agGridRowHeight() {
      return parseInt(styleVars['rowHeight' + this.fontSize.toUpperCase()]);
    },
    renderers() {
      return [TickFlashRenderer];
    },
    columnDefinition() {
      return this.columns.map(e => {
        return Object.assign({}, e, {
          headerName: this.$t(e.headerName)
        });
      });
    },
    rowGroupsOpenValue() {
      return this.$store.state.widget[this.wid].rowGroupsOpen;
    },
    // Option-Chain Graph
    graphViewValue() {
      return this.$store.state.widget[this.wid].graphView;
    },
    graphViewOptions() {
      return [
        { value: 'all', text: this.i18n(`graph.controls.all`) },
        { value: 'calls', text: this.i18n(`graph.controls.calls`) },
        { value: 'puts', text: this.i18n(`graph.controls.puts`) }
      ];
    },
    expiriesValue() {
      return this.$store.state.widget[this.wid].expiries;
    },
    expiries() {
      if (!this.allExpiries.length) return [];

      return this.expiriesValue
        .filter(exp =>
          this.allExpiries.find(e => e.id === exp && this.isExpiryVisible(this.groupModel[e.id].expiryType))
        )
        .map(expiry => this.allExpiries.find(e => e.id === expiry));
    },
    expiriesComplete() {
      return this.allExpiries.filter(e => {
        return (
          e.text.toLowerCase().indexOf(this.expiryInput.toLowerCase()) !== -1 &&
          this.isExpiryVisible(this.groupModel[e.id].expiryType)
        );
      });
    },
    graphFidTopValue() {
      return this.$store.state.widget[this.wid].graphFidTop;
    },
    graphFidBottomValue() {
      return this.$store.state.widget[this.wid].graphFidBottom;
    },
    graphFidOptions() {
      return [
        { value: 'q.OPINT_1', text: this.i18n(`templates.fids.OPINT_1`) },
        { value: '_IMP_VOL', text: this.i18n(`templates.fids._IMP_VOL`) },
        { value: '_EXTRINSIC', text: this.i18n(`templates.fids._EXTRINSIC`) },
        { value: 'q._ACVOL_1', text: this.i18n(`templates.fids._ACVOL_1`) },
        { value: '_DELTA', text: this.i18n(`templates.fids._DELTA`) },
        { value: '_GAMMA', text: this.i18n(`templates.fids._GAMMA`) },
        { value: '_VEGA', text: this.i18n(`templates.fids._VEGA`) },
        { value: '_THETA', text: this.i18n(`templates.fids._THETA`) },
        { value: '_RHO', text: this.i18n(`templates.fids._RHO`) },
        { value: '_INTRINSIC', text: this.i18n(`templates.fids._INTRINSIC`) }
      ];
    },
    graphChartTopType() {
      return this.graphFidTopValue === 'q.OPINT_1' ? 'scatter' : 'line';
    },
    graphChartBottomType() {
      return this.graphFidBottomValue === 'q.OPINT_1' ? 'scatter' : 'line';
    },
    // General
    gridUpdateInterval() {
      return this.$store.state.applicationSettings.general.settings.quoteSpeed;
    },
    i18nNamespace() {
      return 'white-label.widgets.optionChain';
    },
    locale() {
      return this.$store.state.applicationSettings.general.settings.applicationLanguage;
    },
    displayedEarningsDate() {
      moment.locale(this.$i18n.locale);
      let earningsDate = this.nextEarningsDate ? moment(this.nextEarningsDate).format('MMMM Do, Y') : '';
      return `${earningsDate} ${
        this.nextEarningsDate
          ? '(' +
            moment(this.nextEarningsDate).diff(this.momentTZ().startOf('day'), 'days') +
            ' ' +
            this.$t(`white-label.globalLabels.days`) +
            ')'
          : '-'
      }`;
    },
    streamingActiveValue: {
      get() {
        return this.$store.state.widget[this.wid].streamingActive;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/CALCULATION_STREAMING_ACTIVE_CHANGED`, value);
      }
    },
    stockPrice: {
      get() {
        return this.$store.state.widget[this.wid].stockPrice;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/STOCK_PRICE_CHANGED`, value);
      }
    },
    isCalculationApplied() {
      return (
        this.stockPrice === this.calculationParameters.stockPrice &&
        this.impVola === this.calculationParameters.impVola &&
        this.targetDate.getDate() === this.calculationParameters.targetDate.getDate() &&
        this.targetDate.getMonth() === this.calculationParameters.targetDate.getMonth() &&
        this.targetDate.getFullYear() === this.calculationParameters.targetDate.getFullYear()
      );
    },
    impVola: {
      get() {
        return this.$store.state.widget[this.wid].impVola;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/IMP_VOLA_CHANGED`, value);
      }
    },
    targetDate: {
      get() {
        return this.$store.state.widget[this.wid].targetDate;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/TARGET_DATE_CHANGED`, value);
      }
    }
  },
  methods: {
    // Option-Chain
    onSetRIC(ric) {
      this.$store.commit(`widget/${this.wid}/RIC_CHANGED`, ric);
      this.updateLink(ric);
    },
    resetOptionChain() {
      this.$store.commit(`widget/${this.wid}/EXPIRIES_CHANGED`, []);

      this.deleteAllLegs();
      this.$store.commit(`widget/${this.wid}/REMOVE_LEG`, 'first');
      this.$store.commit(`widget/${this.wid}/REMOVE_LEG`, 'second');

      this.$store.commit(`widget/${this.wid}/ROWGROUPS_CHANGED`, []);

      this.timeseries.intradayData = null;
      this.timeseries.historicalData = null;

      this.resetOptionChainGraphCharts();

      this.chartTopC = {};
      this.chartTopP = {};
      this.chartBottomC = {};
      this.chartBottomP = {};

      this.optionChainInitialized = false;

      this.optionsAvailable = false;
    },
    // Option-Chain -- DATA -- Fetch
    fetch() {
      this.hasError = false;
      this.hasStreamingError = false;
      //this.isLoading = true;
      this.optionsAvailable = false;

      this.optionsSnapshots = [];

      this.streamingUnsubscribe(this.symbolSubscriptionId).then(this.symbolStreamingSubscribe);

      if (!this.optionChainInitialized) {
        this.initUnderlyingChart();
      }

      if (this.gridOptions.api) {
        this.gridOptions.api.showLoadingOverlay();
      }

      this.streamingUnsubscribe(this.optionsSubscriptionId)
        .then(this.getUnderlyingData)
        .then(this.getEarningsUnderlyingData)
        .then(() => {
          return getInterestRate(this.underlyingData['q._COUNTRY'], this.underlyingData['q._CURRENCY']).then(
            interestData => (this.interestRateData = interestData)
          );
        })
        .then(this.getOptionChain)
        .then(response => {
          this.createGroupModel(response.data);

          this.validateExpiries();
          this.validateRowGroups();
          this.validateLegs();

          this.streamingRics = this.collectOptionsStreamingRICs();

          this.initOptionChain();

          if (this.streamingRics.length > 0) {
            this.optionsStreamingSubscribe(this.streamingRics);
          } else {
            const rowData = this.createRows([]);
            this.dataGridSet(rowData);
            this.optionChainInitialized = true;
            //this.optionsAvailable = false;
            this.isLoading = false;
          }
        });
    },
    getUnderlyingData() {
      return API.Rest.get(
        `/quote/info?rics=${this.ric}&fids=q.RIC,q._ASK,q._BID,q._TRDPRC_1,q._CURRENCY,q._COUNTRY,q._YIELD,q._DSPLY_NAME,q.ASKSIZE,q.BIDSIZE,q._ACVOL_1,q._OPEN_PRC,q._HIGH_1,q._LOW_1,q._NETCHNG_1,q._PCTCHNG,q.TRD_STATUS,rkd.RECOM_MEAN_CURR,rkd4.TARGETPRICE,q._HST_CLOSE,q.YRHIGH,q.YRLOW,rkd.PRICE_TO_EPS,rkd.EPS,q.DIVIDEND`
      ).then(response => {
        for (let key in response.data.metadata) {
          this.underlyingMetadata[key] = response.data.metadata[key].type;
        }
        this.underlyingData = response.data.data[0];
        // NOTE: 'getCurrencyData' needs to be executed first to have 'this.currencyData.symbol' in following line
        this.currencyData = getCurrencyData(this.underlyingData['q._COUNTRY'], this.underlyingData['q._CURRENCY']);
        this.underlyingData['_CURR_SYMBOL'] = this.currencyData.symbol;

        // compute recomendation text based on recom value
        const recommendationValue = Number.parseFloat(this.underlyingData['rkd.RECOM_MEAN_CURR']);
        let recomText = '';
        if (!isNaN(recommendationValue)) {
          if (recommendationValue < 1.5) recomText = 'strong-buy';
          else if (recommendationValue < 2.5) recomText = 'buy';
          else if (recommendationValue < 3.5) recomText = 'hold';
          else if (recommendationValue < 4.5) recomText = 'sell';
        }
        this.underlyingData['recomText'] = recomText;

        // Update tradePrice for 'Theoretical Calculation Panel' once to have an initial value close to stocks price
        // this.stockPrice = parseFloat(this.underlyingData['q._TRDPRC_1']);

        return response['q._CURRENCY'];
      });
    },
    getEarningsUnderlyingData() {
      API.Rest.get(
        `/company/earnings?rics=${this.ric}&fids=earn.COMBI_DATE,earn.MARKET_PHASE&fromDate=${moment().format(
          'YYYY-MM-DD'
        )}`
      ).then(res => {
        this.nextEarningsDate = res.data.data[0] ? res.data.data[0]['earn.COMBI_DATE'] : '';
        let marketPhase = res.data.data[0] ? res.data.data[0]['earn.MARKET_PHASE'] : '';
        this.nextMarketPhase = isNaN(marketPhase) ? '' : marketPhase;
      });
    },
    getOptionChain() {
      const strikePriceRange = this.getStrikePriceRange(this.underlyingData['q._TRDPRC_1']);
      const strikePriceMin = strikePriceRange.min !== '' ? `&strikePriceMin=${strikePriceRange.min}` : '';
      const strikePriceMax = strikePriceRange.max !== '' ? `&strikePriceMax=${strikePriceRange.max}` : '';

      const fids = Object.keys(this.optionsFids)
        .concat(this.tableFids.C)
        .concat(this.tableFids.P)
        .concat(this.mandatoryFids);

      return API.Rest.get(
        `/find/options?fids=${fids}&ulname=${this.ric}&searchFor=UL_RIC${strikePriceMin}${strikePriceMax}&isNF=false&optionTypes=OPT`
      ).then(response => {
        if (response.data.data.length === 0) {
          this.deleteAllLegs();
          this.dataGridSet(response.data.data);
          this.interestRateData = {};

          this.optionsAvailable = false;
          this.isLoading = false;

          throw new Error('NO_OPTIONS_RECIEVED');
        }

        // Update metadata
        for (let key in response.data.metadata) {
          let rawKey = key.split('.')[1];
          rawKey = rawKey.charAt(0) === '_' ? rawKey : '_' + rawKey;
          this.formats[`C${rawKey}`] = response.data.metadata[key].type;
          this.formats[`P${rawKey}`] = response.data.metadata[key].type;

          this.optionsFids[key]
            ? (this.optionsFids[key].formatter = response.data.metadata[key].type)
            : (this.optionsStreamingFids[key].formatter = response.data.metadata[key].type);
        }

        this.optionsFids['xopt._STRIKE'].formatter = 'float.price';

        // TDWATP-8783 hardcoded exception for SPX/SPXW. Backend returns SPX weekly options marked as monthly
        if (this.ric === '.SPX') {
          response.data.data.forEach(el => {
            if (el['xopt.EXPIR_CYCL'].startsWith('M') && el['x.RIC'].startsWith('SPXW')) el['xopt.EXPIR_CYCL'] = 'W';
          });
        }

        return response.data;
      });
    },

    // Option-Chain -- DATA -- Streaming
    streamingConnect() {
      return new Promise((resolve, reject) => {
        if (!API.Streaming.isConnected) {
          API.Streaming.connect({
            timeout: 10000,
            monitor: {
              interval: 10000,
              timeout: 5000
            },
            visibilityMonitorEnabled: true
          }).then(resolve, reject);
        } else {
          resolve();
        }
      });
    },
    streamingUnsubscribe(id) {
      return new Promise((resolve /*, reject */) => {
        if (id !== null) {
          API.Streaming.Quote.unsubscribe(id, (/*e, err*/) => {
            /*
            if (err) {
              reject(err);
            } else {
              id = null;
              resolve(e);
            }
            */
            id = null;
            resolve();
          });
        } else {
          resolve();
        }
      });
    },
    symbolStreamingSubscribe() {
      this.symbolSubscriptionId = API.Streaming.Quote.subscribe(
        {
          rics: [this.ric],
          fids: streamableFidsFilter([
            'q.RIC',
            'q._ASK',
            'q._BID',
            'q._TRDPRC_1',
            'q._NETCHNG_1',
            'q._PCTCHNG',
            'q._CURRENCY',
            'q._COUNTRY',
            'q._YIELD',
            'q.DELTA',
            'q.GAMMA',
            'q.THETA',
            'q.RHO',
            'q.VEGA'
          ])
        },
        // Subscribe Callback
        (_response, error) => {
          //console.log('symbolStreaming subscribe', response, error);
          if (error) {
            this.hasStreamingError = true;
            console.warn('Symbol streaming subscription error', error);
          } else {
            this.hasStreamingError = false;
          }
        },
        // Snapshot Callback
        (response, error) => {
          //console.log('symbolStreaming snapshot', response.payload[0]);
          if (error) {
            this.hasStreamingError = true;
            console.warn('Symbol streaming snapshot error', error);
            return;
          }

          this.hasStreamingError = false;

          // Update this.underlyingData
          this.underlyingData = Object.assign(this.underlyingData, response.payload[0].data);
        },
        // Update Callback
        (response, error) => {
          if (error) {
            this.hasStreamingError = true;
            console.warn('Symbol streaming update error', error);
            return;
          }

          this.hasStreamingError = false;

          try {
            const tradePriceChanged =
              response.payload[0].data['q._TRDPRC_1'] &&
              response.payload[0].data['q._TRDPRC_1'] !== this.underlyingData['q._TRDPRC_1']
                ? true
                : false;

            this.underlyingData = Object.assign(this.underlyingData, response.payload[0].data);

            if (this.optionsAvailable && tradePriceChanged && this.streamingRics.length) {
              // All values of all options need to be re-calculated
              this.updateAllOptions();

              // Update stockPrice in calculation if streamingActiveValue to have latest stockPrice in input if user changes to template 'theoretical'
              // if (this.streamingActiveValue) {
              //   this.stockPrice = parseFloat(this.underlyingData['q._TRDPRC_1']).toFixed(2);
              // }

              this.updateGraphData();
            }
          } catch (e) {
            console.error(e);
          }
        }
      );
    },
    optionsStreamingSubscribe(options) {
      this.optionsSubscriptionId = API.Streaming.Quote.subscribe(
        {
          rics: options,
          fids: streamableFidsFilter(Object.keys(this.optionsStreamingFids))
        },
        // Subscribe Callback
        (response, error) => this.handleSubscription(response, error),
        // Snapshot Callback
        (response, error) => this.handleSnapshot(response, error),
        // Update Callback
        (response, error) => this.handleUpdate(response, error)
      );
    },

    handleSubscription(_response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Options streaming subscription error', error);
      } else {
        this.hasStreamingError = false;
      }
    },
    handleSnapshot(response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Options streaming snapshot error', error);
        return;
      }

      this.hasStreamingError = false;

      this.handleUpdate(response, error);
    },
    handleUpdate(response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Options streaming update error', error);
        return;
      }

      this.hasStreamingError = false;

      response = response.payload[0];

      try {
        const rowID = this.options[response.ric].ROW_ID;
        const rowNode = this.gridOptions.api.getRowNode(rowID);

        if (!rowNode) return;

        const existingOption = this.options[response.ric];

        // Create updated option from original one in this.options enhanced with response.data
        const updatedOption = Object.assign(existingOption, response.data);

        // If response.data contains 'q._VOLA_BID' or 'q._VOLA_ASK' option needs to be re-calculated
        if (response.data['q._VOLA_ASK'] || response.data['q._VOLA_BID']) {
          //this.calculateOption(response.ric, updatedOption);
          const calculatedOption = this.calculateOption(updatedOption);
          this.updateOption(calculatedOption);
        } else {
          // Simply update option with values in update-response (Probably 'q._BID' or 'q._ASK' etc...)
          this.updateOption(updatedOption);
        }

        // Create newRowData from updated option
        const newRowData = this.createRowData(this.options[response.ric]);

        // Update old rowData with new rowData
        const updatedRow = Object.assign(rowNode.data, newRowData);

        // Update row in grid with updatedRow
        this.dataGridUpdate([updatedRow]);
      } catch (e) {
        console.error(e);
      }
    },
    collectOptionsStreamingRICs() {
      let result = [];

      this.rowGroupsOpenValue.forEach(groupID => {
        if (this.groupModel[groupID] && this.isExpiryVisible(this.groupModel[groupID].expiryType)) {
          result = result.concat(this.groupModel[groupID].rics);
        }
      });

      if (this.legsValue.first && this.legsValue.first.type === 'OPT') {
        if (result.indexOf(this.legsValue.first.RIC) === -1) {
          result.push(this.legsValue.first.RIC);
        }
      }
      if (this.legs.second && isOption(this.legs.second)) {
        if (result.indexOf(this.legs.second.option['x.RIC']) === -1) {
          result.push(this.legs.second.option['x.RIC']);
        }
      }

      // If OptionChainGraph active respect row-group in expiriesValue also
      if (this.isOptionChainGraphActive()) {
        this.expiriesValue.forEach(expiryID => {
          if (this.groupModel[expiryID] && this.isExpiryVisible(this.groupModel[expiryID].expiryType)) {
            result = result.concat(this.groupModel[expiryID].rics);
          }
        });
      }

      return result;
    },
    adjustOptionsStreaming() {
      //this.hasStreamingError = false; // ?!
      this.optionsAvailable = false;
      this.optionsSnapshots = []; // ?!

      this.streamingUnsubscribe(this.optionsSubscriptionId).then(() => {
        this.streamingRics = this.collectOptionsStreamingRICs();

        this.initOptionChain();

        if (this.streamingRics.length > 0) {
          this.optionsStreamingSubscribe(this.streamingRics);
        } else {
          if (!this.isOptionChainGraphActive()) {
            this.autoSizeColumns();
          }
          // Create data of closed groups - otherwise table would be empty
          const rowData = this.createRows([]);
          this.dataGridSet(rowData);
        }
      });
    },
    initOptionChain() {
      // Collect snapshots to be rendered initially
      const snapshots = this.streamingRics.map(ric => ({
        'q.RIC': ric
      }));

      // Create inital row data and render it
      const rowData = this.createRows(snapshots);
      this.dataGridSet(rowData);

      // Update OptionChainGraph if active
      this.updateGraphData();

      if (!this.isOptionChainGraphActive()) {
        this.autoSizeColumns();
      }

      this.expandRowGroups();

      this.optionChainInitialized = true;
      this.optionsAvailable = true;
      this.isLoading = false;
    },

    // Option-Chain -- DATA -- Model
    createGroupModel(options) {
      this.options = {};
      this.groupModel = {};

      const today = moment();

      options.forEach(o => {
        const maturityISO = o['xopt._MATURITY_ISO'];

        if (maturityISO && maturityISO !== '-') {
          const isAlive = moment(maturityISO).isSameOrAfter(today, 'day');
          const displayName = o['xopt._DSPLY_NAME'];

          if (isAlive && displayName.split(' ')[1]) {
            o['CONTRACT'] = displayName.split(' ')[0]; // needed to mark expiry groups with same date but different contracts
            o['GROUP_ID'] = `${o['CONTRACT']}_${maturityISO}`; // = id needed for row-grouping in ag-grid

            o['_COUNTRY'] = this.underlyingData['q._COUNTRY'];
            o['_CURR_SYMBOL'] = this.currencyData.symbol;
            o['_UNDERLYING_RIC'] = this.underlyingData['q.RIC'];

            let rowID = displayName.substring(0, displayName.length - 1);
            rowID = rowID.replace(/\s/g, '');
            o['ROW_ID'] = `${rowID}-${maturityISO}`; // needed for rowUpdate in ag-grid

            const expiryType =
              o['xopt.EXPIR_CYCL'] !== '-'
                ? this.getExpiryType(o['xopt.EXPIR_CYCL'])
                : this.getExpiryTypeFromDisplayName(displayName);

            const shares =
              !isNaN(o['xopt.LOT_SIZE_A']) && +o['xopt.LOT_SIZE_A'] !== 100 ? `${+o['xopt.LOT_SIZE_A']} Shares, ` : '';

            const isNonStandard = !isNaN(o['CONTRACT'].slice('-1'));

            if (!this.groupModel[o['GROUP_ID']]) {
              this.groupModel[o['GROUP_ID']] = {
                order: [],
                items: {},
                rics: [],
                expiryType,
                shares,
                isNonStandard
              };
            }

            if (!this.groupModel[o['GROUP_ID']].order.includes(o['xopt._STRIKE'])) {
              this.groupModel[o['GROUP_ID']].order.push(o['xopt._STRIKE']);
            }

            if (!this.groupModel[o['GROUP_ID']].items[o['xopt._STRIKE']]) {
              this.groupModel[o['GROUP_ID']].items[o['xopt._STRIKE']] = {
                C: null,
                P: null
              };
            }

            this.groupModel[o['GROUP_ID']].items[o['xopt._STRIKE']][o['xopt._TYPE_CP']] = o['x.RIC'];

            this.options[o['x.RIC']] = o;
          }
        }
      });

      // Sort each group in groupModel by strike-price
      Object.keys(this.groupModel).forEach(groupID => {
        this.groupModel[groupID].order.sort(function compare(a, b) {
          return a - b;
        });

        if (this.strikesValue !== 'all') {
          const strikePrice = +parseFloat(this.underlyingData['q._TRDPRC_1']).toFixed(1);
          const strikeWidth = parseFloat(this.strikesValue) / 2;

          const closestStrikePriceInGroup = this.groupModel[groupID].order.reduce(function(prev, curr) {
            return Math.abs(curr - strikePrice) < Math.abs(prev - strikePrice) && Number(curr) >= strikePrice
              ? curr
              : prev;
          });

          const closestStrikePriceIndex = this.groupModel[groupID].order.indexOf(closestStrikePriceInGroup.toString());
          const strikePriceIndex = closestStrikePriceIndex !== -1 ? closestStrikePriceIndex : 0;

          // Check if interval limits to be sliced are 'outside' the original array, if yes -> adjust interval limits
          const sliceStart = strikePriceIndex - strikeWidth >= 0 ? strikePriceIndex - strikeWidth : 0;
          const sliceStartGap =
            closestStrikePriceIndex - strikeWidth < 0 ? Math.abs(closestStrikePriceIndex - strikeWidth) : 0;

          const sliceEnd =
            strikePriceIndex + strikeWidth + sliceStartGap <= this.groupModel[groupID].order.length
              ? strikePriceIndex + strikeWidth + sliceStartGap
              : this.groupModel[groupID].order.length;

          this.groupModel[groupID].order = this.groupModel[groupID].order.slice(sliceStart, sliceEnd);

          // If order array is greater than the current strikesValue (due to slicing adjustments) downsize it back to strikesValue items
          if (this.groupModel[groupID].order.length > this.strikesValue) {
            this.groupModel[groupID].order = this.groupModel[groupID].order.slice(0, parseInt(this.strikesValue) - 1);
          }
        }

        this.groupModel[groupID].order.forEach(strike => {
          // Ensure to only add complete rows (-> call & put option available for a strike)
          if (this.groupModel[groupID].items[strike].C && this.groupModel[groupID].items[strike].P) {
            this.groupModel[groupID].rics.push(this.groupModel[groupID].items[strike].C);
            this.groupModel[groupID].rics.push(this.groupModel[groupID].items[strike].P);
          }
        });
      });

      this.allExpiries = this.processExpiriesData(Object.keys(this.groupModel));
    },
    /*
     * Loop through options (of current opened rowGroups), find pairs & create rows with correct prefixes (C_ || P_)
     */
    createRows(snapshots) {
      const rows = {};

      snapshots.forEach(o => {
        if (o.hasOwnProperty('q.RIC')) {
          // Update copied original option with snapshot data
          const existingOption = this.options[o['q.RIC']];

          /* !!
           It's possible that the snapshot contains '-' for a value
           -> then the snapshot data 'ruins' the actually 'good' already exsiting data
            -> this case results in rows filled with '-' where only Bid & Ask change via a lter update
          */
          const updatedOption = Object.assign(existingOption, o);

          // Enhance option with all needed (calculated) values for the different templates
          const calculatedOption = this.calculateOption(updatedOption);

          // Update original option with snapshot & calculations
          this.updateOption(calculatedOption);

          const rowID = this.options[o['q.RIC']]['ROW_ID'];

          if (!rows[rowID]) {
            rows[rowID] = {};

            rows[rowID]['xopt._TYPE_CP'] = calculatedOption['xopt._TYPE_CP'];
            rows[rowID]['q._TYPE'] = calculatedOption['q._TYPE'];
            rows[rowID]['xopt._MATURITY_ISO'] = calculatedOption['xopt._MATURITY_ISO'];
            rows[rowID]['xopt._STRIKE'] = calculatedOption['xopt._STRIKE'];
            rows[rowID]['ROW_ID'] = calculatedOption['ROW_ID'];
            rows[rowID]['GROUP_ID'] = calculatedOption['GROUP_ID'];
          }

          // Possible existingRowData state: {} or an partially filled object from an already found mathing option
          const existingRowData = rows[rowID];

          // Create row data for row in its current state (-> C-Option, P-Option or complete pair C + P + RowBaseData (xopt._TYPE_CP, etc.))
          const row = this.createRowData(calculatedOption);

          // NOTE: Here exceptionally the existing data should get merged into the currently created
          rows[rowID] = Object.assign(row, existingRowData);
        }
      });

      // Add at least one of all the other currently not opened rows/options to be able to render and
      // group them in grid via rowGroupRenderer
      Object.keys(this.groupModel).forEach(groupID => {
        if (this.isExpiryVisible(this.groupModel[groupID].expiryType)) {
          if (this.groupModel[groupID].rics.length) {
            if (!rows[this.options[this.groupModel[groupID].rics[0]].ROW_ID]) {
              rows[this.options[this.groupModel[groupID].rics[0]].ROW_ID] = {
                GROUP_ID: this.options[this.groupModel[groupID].rics[0]].GROUP_ID,
                ROW_ID: this.options[this.groupModel[groupID].rics[0]].ROW_ID
              };
            }
          }
        }
      });

      // Create an array from rows object as ag-grid needs an array to set data (rows)
      const rowsArray = Object.keys(rows).map(r => rows[r]);

      return rowsArray;
    },
    /*
     * Creates rowData for every fid in given data. Result properties are then prefixed with given optionType
     */
    createRowData(data) {
      const rowData = {};
      const optionType = data['xopt._TYPE_CP'];

      Object.keys(data).forEach(fid => {
        const realFid = fid.split('.')[1] ? fid.split('.')[1] : fid;
        const formattedData = fid !== 'q.OPINT_1' ? data[fid] : Math.abs(data[fid]); // Due to OPINT_1 often being '-1'
        rowData[`${optionType}${realFid.charAt(0) === '_' ? realFid : '_' + realFid}`] = formattedData;
      });

      return rowData;
    },

    // Option-Chain -- DATA -- Options
    updateOption(option) {
      const ric = option['x.RIC'];
      this.options[ric] = Object.assign(this.options[ric], option);
    },
    updateAllOptions() {
      if (this.options) {
        // NOTE: New!
        const updatedRows = [];

        Object.keys(this.options).forEach(ric => {
          if (this.streamingRics.includes(ric)) {
            // Re-Calculate original option
            //this.calculateOption(ric, this.options[ric]);
            const calculatedOption = this.calculateOption(this.options[ric]);
            this.updateOption(calculatedOption);

            const rowNode = this.gridOptions.api.getRowNode(this.options[ric].ROW_ID);

            const newRowData = this.createRowData(this.options[ric]);

            const updatedRow = Object.assign(rowNode.data, newRowData);

            // Update row in grid with updatedRow
            // Alternative: this.dataGridUpdate([updatedRow]);

            // Collect rows to update
            updatedRows.push(updatedRow);
          }
        });
        if (this.gridOptions && this.gridOptions.api)
          this.gridOptions.api.applyTransactionAsync({ update: updatedRows });
      }
    },

    // Option-Chain -- DATA -- Options -- Calcualtions
    calculateOption(option) {
      const tradePrice = parseFloat(this.underlyingData['q._TRDPRC_1']);
      const interestRate = parseFloat(this.interestRateData['q.PRIMACT_1']); // Math.abs(parseFloat(this.interestRateData['q.PRIMACT_1']));

      // NOTE: Quick fix for index symbols that mostly do not have a yield value
      const _yield = this.underlyingData['q._YIELD'] !== '-' ? parseFloat(this.underlyingData['q._YIELD']) : 0;

      const greeks = this.calculateGreeksForOption(option);

      const intrinsicExtrinsic = this.calculateIntrinsicExtrinsicForOption(option, tradePrice);

      const probability = this.calculateProbabilityForOption(option, tradePrice, interestRate, _yield);

      const theoreticalPrice = this.calculateTheoreticalPriceForOption(option, tradePrice, interestRate, _yield);

      // Update original option with calculated data
      return Object.assign(option, greeks, intrinsicExtrinsic, probability, theoreticalPrice);
    },
    // Calculations for different values of an option (based on template)
    calculateGreeksForOption(option) {
      const result = {};

      // const typeFlag = option['xopt._TYPE_CP'];
      // // underlying price S
      // const S = underlyingPrice;
      // // strike price X
      // const X = parseFloat(option['xopt._STRIKE']);
      // // time to maturity in years
      // const Time = getTimeToMaturityInYears(option['xopt._MATURITY_ISO']);
      // // interest rate r
      // const r = interestRate / 100; // for CAD: CADISC=/PRIMACT_1 = 1.25%; for EUR: ECBMRO=/PRIMACT_1 = 1.25%
      // // dividend yield r_d, e.g. 5% --> 0.05
      // const r_d = _yield / 100;
      // // cost of carry rate
      // const b = r - r_d;
      // // volatility
      // const volatility = Volatility.getImpliedVolatility(option['q._VOLA_ASK'], option['q._VOLA_BID']); // volatility from Fid is given in percentage, e.g. as 29.16%

      // const sigma = volatility / 100; // e.g. 0.2916

      // result._DELTA = Greeks.GBSDelta(typeFlag, S, X, Time, r, b, sigma);
      // result._GAMMA = Greeks.GBSGamma(typeFlag, S, X, Time, r, b, sigma);
      // result._THETA = Greeks.GBSTheta(typeFlag, S, X, Time, r, b, sigma) / 365.0;
      // result._RHO = Greeks.GBSRho(typeFlag, S, X, Time, r, b, sigma) / 100.0;
      // result._VEGA = Greeks.GBSVega(typeFlag, S, X, Time, r, b, sigma) / 100.0;

      result._DELTA = isNaN(option['q.DELTA']) ? '-' : option['q.DELTA'];
      result._GAMMA = isNaN(option['q.GAMMA']) ? '-' : option['q.GAMMA'];
      result._THETA = isNaN(option['q.THETA']) ? '-' : option['q.THETA'];
      result._RHO = isNaN(option['q.RHO']) ? '-' : option['q.RHO'];
      result._VEGA = isNaN(option['q.VEGA']) ? '-' : option['q.VEGA'];

      return result;
    },
    calculateIntrinsicExtrinsicForOption(option, underlyingPrice) {
      const typeFlag = option['xopt._TYPE_CP'];
      const S = underlyingPrice;

      const P = option['q._TRDPRC_1'];
      const X = parseFloat(option['xopt._STRIKE']);

      const intrinsic = this.getIntrinsicValueForOption(typeFlag, S, X);
      const extrinsic = !isNaN(Math.max(P - intrinsic, 0)) ? Math.max(P - intrinsic, 0) : '-';

      return {
        _INTRINSIC: intrinsic,
        _EXTRINSIC: extrinsic
      };
    },
    getIntrinsicValueForOption(typeFlag, S, X) {
      return typeFlag === 'C' ? Math.max(S - X, 0) : typeFlag === 'P' ? Math.max(X - S, 0) : NaN;
    },
    calculateProbabilityForOption(option, underlyingPrice, interestRate, _yield) {
      const result = {};

      const TypeFlag = option['xopt._TYPE_CP'];
      const S = underlyingPrice;
      const X = parseFloat(option['xopt._STRIKE']);
      const Time = getTimeToMaturityInYears(option['xopt._MATURITY_ISO']);
      const r = interestRate / 100;
      const r_d = _yield / 100;

      const volatility = Volatility.getImpliedVolatility(option['q._VOLA_ASK'], option['q._VOLA_BID']);
      const sigma = volatility / 100;

      result._PROB_ITM = Probability.ProbITM(TypeFlag, S, X, Time, r, r_d, sigma) * 100;
      result._PROB_OTM = Probability.ProbOTM(TypeFlag, S, X, Time, r, r_d, sigma) * 100;
      result._PROB_TOUCH = Probability.ProbTouch(TypeFlag, S, X, Time, r, r_d, sigma) * 100;

      return result;
    },
    calculateTheoreticalPriceForOption(option, underlyingPrice, interestRate, _yield) {
      const result = {};

      const TypeFlag = option['xopt._TYPE_CP'];
      const S = !this.streamingActiveValue ? this.stockPrice : underlyingPrice;
      const X = parseFloat(option['xopt._STRIKE']);
      const Time = !this.streamingActiveValue
        ? getTimeToMaturityInYears(option['xopt._MATURITY_ISO'], this.targetDate)
        : getTimeToMaturityInYears(option['xopt._MATURITY_ISO']);
      const r = interestRate / 100;
      const r_d = _yield / 100;
      const b = r - r_d;

      const volatility = Volatility.getImpliedVolatility(option['q._VOLA_ASK'], option['q._VOLA_BID']);
      result['_IMP_VOL'] = volatility;

      const sigma = !this.streamingActiveValue ? (parseFloat(this.impVola) + volatility) / 100 : volatility / 100;

      if (Time <= 1e-4) {
        result['_THEO_PRC'] = TypeFlag === 'C' ? Math.max(S - X, 0) : Math.max(X - S, 0);
      } else {
        const price = Greeks.GBSOption(TypeFlag, S, X, Time, r, b, sigma) || 0;
        result['_THEO_PRC'] = price.toFixed(3);
      }

      return result;
    },

    // Option-Chain -- Quote
    onUpdateQuotePanel() {
      this.$store.commit(`widget/${this.wid}/QUOTE_PANEL_CHANGED`, !this.quotePanelOpenValue);
    },
    buyClicked() {
      if (this.orderTypeValue === 'slo') {
        this.singleLegOrderClick('stock', 'BUY');
      } else {
        this.multiLegOrderClick('STO', null, '_ASK');
      }
    },
    sellClicked() {
      if (this.orderTypeValue === 'slo') {
        this.singleLegOrderClick('stock', 'SELL');
      } else {
        this.multiLegOrderClick('STO', null, '_BID');
      }
    },
    coverClicked() {
      if (this.orderTypeValue === 'slo') {
        this.singleLegOrderClick('stock', 'COVER');
      } else {
        this.multiLegOrderClick('STO', null, '_ASK');
      }
    },
    shortClicked() {
      if (this.orderTypeValue === 'slo') {
        this.singleLegOrderClick('stock', 'SHORT');
      } else {
        this.multiLegOrderClick('STO', null, '_BID');
      }
    },
    alertClicked() {
      this.$store.dispatch('alerts/openAlertDialog', { fromRIC: this.ric, wid: this.wid });
    },

    // Option-Chain -- Quote -- UnderlyingChart
    chartTypeChanged(chartType) {
      this.$store.commit(`widget/${this.wid}/CHART_TYPE_CHANGED`, chartType);
    },
    chartTimespanChanged(chartTimespan) {
      this.$store.commit(`widget/${this.wid}/CHART_TIMESPAN_CHANGED`, chartTimespan);
    },
    initUnderlyingChart() {
      this.fetchTimeseriesIntradayData();
      this.fetchTimeseriesHistoricalData();
    },
    fetchTimeseriesIntradayData() {
      return API.Rest.get(`/timeseries/implvola?ric=${this.ric}`).then(response => {
        this.timeseries.intradayData = response.data.data;
      });
    },
    fetchTimeseriesHistoricalData() {
      return API.Rest.get(
        `/timeseries/historical?ric=${this.ric}&fromDate=${moment()
          .subtract(1, 'years')
          .format(
            'YYYY-MM-DD'
          )}&fids=h._DATE_START%2Ch.OPEN_PRC%2Ch.HIGH_1%2Ch.LOW_1%2Ch.CLOSE_PRC%2Ch._DATE_END%2Ch.ACVOL_1&samples=D`
      ).then(response => {
        this.timeseries.historicalData = response.data.data;
      });
    },

    // Option-Chain -- Controls
    onUpdateExpiryTypes(expiryType, value) {
      this.$store.commit(`widget/${this.wid}/EXPIRY_TYPES_CHANGED`, {
        expiryType,
        value
      });

      this.dataGridSet([]);
      this.adjustOptionsStreaming();

      if (this.isOptionChainGraphActive()) {
        this.resetOptionChainGraphCharts();
        // this.updateGraphData();
      }
    },
    onUpdateStrikes(strikes) {
      this.strikesValue = strikes;
      this.fetch();
    },
    onStrikeFormSubmit() {
      if (this.customStrikesValue) this.onUpdateStrikes(this.customStrikesValue.toString());
    },
    onUpdateOrderType(orderType) {
      this.$store.commit(`widget/${this.wid}/ORDERTYPE_CHANGED`, orderType);
    },
    onUpdateTemplate() {
      const newCols = this.createColumns(this.tableFids);
      if (this.gridOptions.api) {
        this.gridOptions.api.setColumnDefs(newCols);
        this.gridOptions.api.sizeColumnsToFit();
        this.expandRowGroups();
      }
    },
    updateColumns() {
      const newCols = this.createColumns(this.tableFids);
      this.gridOptions.api.setColumnDefs(newCols);
      this.gridOptions.api.sizeColumnsToFit();
    },
    onUpdateView(view) {
      if (view !== this.viewValue) {
        this.$store.commit(`widget/${this.wid}/VIEW_CHANGED`, view);

        this.adjustOptionsStreaming();
      }
    },
    // Option-Chain -- Calculation (theoretical template)
    onUpdateStreamingActive(streamingActive) {
      this.streamingActiveValue = streamingActive;
      if (this.gridOptions && this.gridOptions.api) this.gridOptions.api.refreshCells();

      this.updateAllOptions();
    },
    onInputStockPrice(stockPrice) {
      this.calculationParameters.stockPrice = stockPrice;
    },
    onInputImpVolatility(impVola) {
      impVola = parseFloat(impVola).toFixed(2);

      this.calculationParameters.impVola = impVola;
    },
    onInputTargetDate(targetDate) {
      this.calculationParameters.targetDate = targetDate;
    },
    onApplyCalculation() {
      this.stockPrice = this.calculationParameters.stockPrice;
      this.impVola = parseFloat(this.calculationParameters.impVola).toFixed(2);
      this.targetDate = this.calculationParameters.targetDate;

      this.updateAllOptions();
    },
    onResetCalculation() {
      Object.assign(this.calculationParameters, {
        stockPrice: '0.00',
        impVola: '0.00',
        targetDate: new Date()
      });
      this.onApplyCalculation();
    },

    // Option-Chain -- Analysis
    onUpdateAnalysisPanel() {
      this.$store.commit(`widget/${this.wid}/ANALYSIS_PANEL_CHANGED`, !this.analysisPanelOpenValue);
    },
    // Option-Chain -- Analysis -- PayOffChart

    // Option-Chain -- Analysis -- KeyMetrics

    // Option-Chain -- Analysis -- Greeks

    // Option-Chain -- Order -- Legs
    addOptionToLegs(optionLeg) {
      // Check which leg-position is still available & if "option" is a stock,
      // because Stock needs to be always firstLeg & only one Stock is allowed
      if (!this.legs.first) {
        this.legs.first = optionLeg;
        this.legs.first.step = this.$store.state.applicationSettings.orders.settings.options.defaultOrderIncrement;
        this.$store.commit(`widget/${this.wid}/ADD_LEG`, {
          which: 'first',
          leg: { type: 'OPT', action: optionLeg.action, quantity: optionLeg.quantity, RIC: optionLeg.option['x.RIC'] }
        });
      } else if (!this.legs.second) {
        this.legs.second = optionLeg;
        this.legs.second.step = this.$store.state.applicationSettings.orders.settings.options.defaultOrderIncrement;
        this.$store.commit(`widget/${this.wid}/ADD_LEG`, {
          which: 'second',
          leg: { type: 'OPT', action: optionLeg.action, quantity: optionLeg.quantity, RIC: optionLeg.option['x.RIC'] }
        });
      }
    },
    addStockToLegs(stockLeg) {
      if (!this.legs.first) {
        this.legs.first = stockLeg;
        this.$store.commit(`widget/${this.wid}/ADD_LEG`, {
          which: 'first',
          leg: { type: 'STO', action: stockLeg.action, quantity: stockLeg.quantity, RIC: this.underlyingData['q.RIC'] }
        });
      } else {
        if (this.legs.first.assetClass.id === 1) {
          const tmpLeg = this.legs.first;
          this.legs.second = tmpLeg;
          this.legs.first = stockLeg;
          this.$store.commit(`widget/${this.wid}/ADD_LEG`, {
            which: 'first',
            leg: {
              type: 'STO',
              action: stockLeg.action,
              quantity: stockLeg.quantity,
              RIC: this.underlyingData['q.RIC']
            }
          });
          this.$store.commit(`widget/${this.wid}/ADD_LEG`, {
            which: 'second',
            leg: { type: 'OPT', action: tmpLeg.action, quantity: tmpLeg.quantity, RIC: tmpLeg.option['x.RIC'] }
          });
        } else {
          this.legs.first = stockLeg;
          this.$store.commit(`widget/${this.wid}/ADD_LEG`, {
            which: 'first',
            leg: {
              type: 'STO',
              action: stockLeg.action,
              quantity: stockLeg.quantity,
              RIC: this.underlyingData['q.RIC']
            }
          });
        }
      }
      this.legs.first.step = this.$store.state.applicationSettings.orders.settings.stocks.defaultOrderIncrement;
    },
    /*
    addLegToState(type, RIC) {
      this.$store.commit(`widget/${this.wid}/ADD_LEG`, {
        which: 'first',
        leg: { type, RIC }
      });
    },
    */
    validateLegs() {
      if (this.legsValue.first || this.legsValue.second) {
        const firstLegInState = this.legsValue.first;

        if (firstLegInState) {
          if (firstLegInState.type === 'OPT') {
            const isOptionStillValid = this.options[firstLegInState.RIC];
            if (isOptionStillValid) {
              // Actually newly calculate option and then update it
              //this.calculateOption(firstLegInState.RIC, this.options[firstLegInState.RIC]);

              const calculatedOption = this.calculateOption(this.options[firstLegInState.RIC]);
              this.updateOption(calculatedOption);

              this.legs.first = new OptionLeg(
                firstLegInState.action,
                firstLegInState.quantity,
                //this.options[firstLegInState.RIC]
                calculatedOption
              );
            } else {
              this.$store.commit(`widget/${this.wid}/REMOVE_LEG`, 'first');
            }
          } else {
            const isStockStillValid = this.underlyingData['q.RIC'] === firstLegInState.RIC;
            if (isStockStillValid) {
              this.legs.first = new StockLeg(firstLegInState.action, firstLegInState.quantity, this.underlyingData);
            } else {
              this.$store.commit(`widget/${this.wid}/REMOVE_LEG`, 'first');
            }
          }
        }

        // NOTE: if secondLegInState leg can only be an option
        const secondLegInState = this.legsValue.second;
        if (secondLegInState) {
          const isOptionStillValid = this.options[secondLegInState.RIC];
          if (isOptionStillValid) {
            //this.calculateOption(secondLegInState.RIC, this.options[secondLegInState.RIC]);

            const calculatedOption = this.calculateOption(this.options[secondLegInState.RIC]);
            this.updateOption(calculatedOption);

            this.legs.second = new OptionLeg(
              secondLegInState.action,
              secondLegInState.quantity,
              //this.options[secondLegInState.RIC]
              calculatedOption
            );
          } else {
            this.$store.commit(`widget/${this.wid}/REMOVE_LEG`, 'second');
          }
        }
      }
    },
    handleLegsChanged() {
      // If one or more values within a leg (the option/stock, quantity, action) have changed re-calculate range prices
      this.updateOrderPriceRange();
    },
    deleteLeg(leg) {
      this.legs[leg] = null;
      this.$store.commit(`widget/${this.wid}/REMOVE_LEG`, leg);
    },
    deleteAllLegs() {
      this.legs.first = null;
      this.legs.second = null;
    },
    updateLegAction(leg, action) {
      if (this.legs[leg]) {
        this.legs[leg].action = getLegAction(action);
      }
    },
    updateLegQuantity(leg, quantity) {
      this.legs[leg].quantity = quantity;
    },

    // Option-Chain -- Order -- Pricing
    onInputOrderPriceRange() {
      // If the range slider has been moved
      this.orderPriceLocked = true;
      this.checkOrderPriceOutOfRange();
    },
    onDecrementValue() {
      const val = (parseFloat(Math.abs(this.orderPriceRangeValue)) - 0.01).toFixed(2);
      this.onChangeOrderPriceInput(val);
    },
    onIncrementValue() {
      const val = (parseFloat(Math.abs(this.orderPriceRangeValue)) + 0.01).toFixed(2);
      this.onChangeOrderPriceInput(val);
    },
    // If a price has been input manually into range price input
    onChangeOrderPriceInput(value) {
      this.orderPriceLocked = true;

      if (!value || value === 0) {
        // Invalid value has been entered manually -> reset createOrderButton & Sign but keep on calculating mid/nat range
        this.orderPriceRangeSign = 'EV';
        this.orderPriceRangeButton = 'primary';
      } else {
        // valid value has been entered manually (again) -> calc sign,createOrderButton and outOfRange ONCE again (to get color again) but keep orderPrice LOCKED!
        this.orderPriceRangeSign = this.detectDRCR(this.orderPriceRangeMidValue, this.orderPriceRangeNatValue);
        this.updateCreateOrderButton();
        this.orderPriceRangeValue =
          this.orderPriceRangeSign === 'CR' ? value.toFixed(2) * -1 : parseFloat(value).toFixed(2);
        this.checkOrderPriceOutOfRange();
      }
    },
    onLockPriceClicked() {
      this.orderPriceLocked = !this.orderPriceLocked;

      // If the price has been unlocked again re-calculate range prices because price could have gone out of range while being locked
      if (!this.orderPriceLocked) {
        this.updateOrderPriceRange();
      }
    },
    onOrderPriceRangeSignClicked() {
      this.orderPriceLocked = true;
      // NOTE: -> also lock price first to make netDebit trigger in KeyMetrics? -> MM2 ?
      this.orderPriceRangeSign = this.orderPriceRangeSign === 'CR' ? 'DR' : 'CR';
      this.updateCreateOrderButton();
    },
    onInputMarketActive(isActive) {
      this.marketActive = isActive;
      this.orderPriceRangeValue = null;
      this.orderPriceLocked = false;
      this.orderPriceRangeSign = 'EV';
      this.orderPriceRangeButton = 'primary';

      // If market checkbox has been unchecked again range prices need to be re-calculated and stored into its input fields
      if (!isActive) {
        this.updateOrderPriceRange();
      }
    },
    onUpdateStrategy(strategy) {
      this.currentStrategy = strategy;
    },
    preloadOrderEntry() {
      import(/* webpackChunkName: "request" */ `@/widgets/WOrderEntryWidget/WOrderEntryWidget.vue`);
    },
    onCreateOrderClicked() {
      // *********************************************************************************
      // NOTE: All values needed for the order ticket are available in 'this.legs' object
      //       when create-order button was clicked.
      //       Therefore the Option Chain Widget itself DOES NOT! need to be changed !!
      // *********************************************************************************
      // collect leg data from this.legs, process in some extra variables and open Order Ticket with prepared values

      const firstOrder = this.legs.first;
      const secondOrder = this.legs.second;

      const isFirstOrderOption = firstOrder.option ? true : false;

      const firstOrderTicketType = isFirstOrderOption ? 'option' : 'stock';
      const firstOrderSymbolRIC = isFirstOrderOption ? firstOrder.option['q.RIC'] : firstOrder.stock['q.RIC'];
      const secondOrderSymbolRIC = secondOrder.option['q.RIC'];

      const firstOrderUnderlyingRIC = this.ric;
      const secondOrderUnderlyingRIC = this.ric;

      const firstOrderAction = isFirstOrderOption
        ? firstOrder.action.name
        : firstOrder.action.name.startsWith('buy')
        ? 'buy'
        : 'sell';
      const secondOrderAction = secondOrder.action.name;

      const firstOrderQuantity = +firstOrder.quantity;
      const secondOrderQuantity = +secondOrder.quantity;

      let strategy = this.currentStrategy.split(' ').join('');
      if (strategy.startsWith('Closing')) {
        strategy = strategy.replace('Closing', '');
      }
      strategy = strategy.charAt(0).toLowerCase() + strategy.substring(1);
      if (strategy === 'customstrategy') {
        if (isFirstOrderOption) {
          strategy = 'customOptionOption';
        } else {
          strategy = 'customStockOption';
        }
      }

      const limitPrice = this.priceInputValue;
      const netCreditDebit = this.orderPriceRangeSign;

      const firstLeg = {
        action: firstOrderAction,
        quantity: firstOrderQuantity,
        ticketType: firstOrderTicketType,
        underlying: { ric: firstOrderUnderlyingRIC },
        symbol: { ric: firstOrderSymbolRIC }
      };

      const secondLeg = {
        action: secondOrderAction,
        quantity: secondOrderQuantity,
        ticketType: 'option',
        underlying: { ric: secondOrderUnderlyingRIC },
        symbol: { ric: secondOrderSymbolRIC }
      };

      this.$widget.openOrderEntry({
        //Need to verify
        orderMode: {},
        legs: {
          firstOrder: firstLeg,
          secondOrder: secondLeg
        },
        strategy: {
          type: strategy,
          limit: limitPrice,
          priceType: this.marketActive ? 'market' : this.orderPriceRangeSign === 'EV' ? 'even' : 'limit', // set priceType to 'even', 'market' or 'limit'
          netCreditDebit: netCreditDebit
        }
      });
    },
    handleOrderPriceRangeValue() {
      this.priceInputValue = this.orderPriceRangeValue ? Math.abs(this.orderPriceRangeValue) : 0;
    },
    updateOrderPriceRange() {
      const firstLeg = this.legs.first;
      const secondLeg = this.legs.second;

      if (firstLeg && secondLeg) {
        let mid = null;
        let nat = null;

        /*
         * NOTE: Because of the structure of leg-objects used in option-chain being different from the leg-objects used in WOrderEntryWidget (and its subcomponents)
         * additional creation of stockOptionLeg & optionLeg from firstLeg/secondLeg is needed to be able to use mid/nat-calculations
         * used in WOrderEntryWidget also -> to have only one calculation through out the application.
         *
         */
        const firstLegType = isOption(firstLeg) ? 'option' : 'stock';
        const stockOptionLeg = {
          bid: +firstLeg[firstLegType]['q._BID'],
          ask: +firstLeg[firstLegType]['q._ASK'],
          actionType: {
            isBuy: () => isLegBuy(firstLeg),
            isSell: () => isLegSell(firstLeg)
          },
          lotSize: isOption(firstLeg) ? +firstLeg.option['xopt.LOT_SIZE_A'] : 1,
          quantity: +firstLeg.quantity
        };
        const optionLeg = {
          bid: +secondLeg.option['q._BID'],
          ask: +secondLeg.option['q._ASK'],
          actionType: {
            isBuy: () => isLegBuy(secondLeg),
            isSell: () => isLegSell(secondLeg)
          },
          lotSize: +secondLeg.option['xopt.LOT_SIZE_A'],
          quantity: +secondLeg.quantity
        };

        if (isOption(firstLeg) && isOption(secondLeg)) {
          const midNat = Price.calculateMidNatForOptionOption(stockOptionLeg, optionLeg);

          mid = midNat.mid;
          nat = midNat.nat;
        } else {
          const midNat = Price.calculateMidNatForStockOption(stockOptionLeg, optionLeg);

          mid = midNat.mid;
          nat = midNat.nat;
        }

        // Always update Mid & Nat prices
        this.orderPriceRangeMidValue = parseFloat(mid).toFixed(2);
        this.orderPriceRangeNatValue = parseFloat(nat).toFixed(2);

        // Only update current range price if market checkbox NOT active And range price is NOT locked or hasnt been set yet
        this.orderPriceRangeValue =
          !this.marketActive && (this.orderPriceRangeValue == null || !this.orderPriceLocked)
            ? parseFloat(nat).toFixed(2)
            : this.orderPriceRangeValue;

        // Get and set latest sign (DR,CR,EV)
        if (!this.orderPriceLocked) {
          this.orderPriceRangeSign = this.detectDRCR(mid, nat);
        }
        this.updateCreateOrderButton();
        this.checkOrderPriceOutOfRange();
      } else {
        this.resetOrderPriceRange();
      }
    },
    updateCreateOrderButton() {
      if (this.orderPriceRangeSign === 'DR') {
        this.orderPriceRangeButton = 'buy';
      } else if (this.orderPriceRangeSign === 'CR') {
        this.orderPriceRangeButton = 'sell';
      } else {
        this.orderPriceRangeButton = 'primary';
      }
    },
    resetOrderPriceRange() {
      this.orderPriceRangeValue = null;
      this.orderPriceRangeMidValue = null;
      this.orderPriceRangeNatValue = null;
      this.orderPriceLocked = false;
      this.orderPriceOutOfRangeMid = false;
      this.orderPriceOutOfRangeNat = false;
      this.orderPriceRangeSign = 'EV';
      this.orderPriceRangeButton = 'primary';
    },
    detectDRCR(mid, nat) {
      return this.marketActive || (mid === 0 && nat === 0) ? 'EV' : Math.abs(mid) > Math.abs(nat) ? 'CR' : 'DR';
    },
    checkOrderPriceOutOfRange() {
      // Reset outOfRange indicators
      this.orderPriceOutOfRangeMid = this.orderPriceOutOfRangeNat = false;

      // If current price is locked check if its out of range meanwhile
      if (this.orderPriceLocked) {
        if (parseFloat(this.orderPriceRangeValue) < parseFloat(this.orderPriceRangeMidValue)) {
          this.orderPriceOutOfRangeMid = true;
        } else if (parseFloat(this.orderPriceRangeValue) > parseFloat(this.orderPriceRangeNatValue)) {
          this.orderPriceOutOfRangeNat = true;
        }
      }
    },

    // Option-Chain -- AG-Grid
    onGridReady(params) {
      params.api.sizeColumnsToFit();
      this.setTableRowsHeight();
      this.$el.querySelector('.ag-header-container').addEventListener('keydown', this.onHeaderRowKeydown);
      this.$el.querySelectorAll('.ag-header-group-cell').forEach(el => {
        el.setAttribute('role', 'cell');
      });
      this.gridOptions.api.setGridAriaProperty('label', this.i18n('widget-title'));
    },
    dataGridSet(rows) {
      if (this.gridOptions.api) this.gridOptions.api.setRowData(rows);
    },
    dataGridUpdate(rows) {
      this.$el.querySelectorAll('.ag-row-group').forEach(el => {
        el.setAttribute('role', 'gridcell');
      });
      if (this.gridOptions && this.gridOptions.api) this.gridOptions.api.applyTransactionAsync({ update: rows });
    },
    autoSizeColumns() {
      if (!this.gridOptions.api) return;
      this.gridOptions.api.sizeColumnsToFit();
      this.gridOptions.api.forEachDetailGridInfo(detailGridApi => {
        detailGridApi.api.sizeColumnsToFit();
      });
    },
    fidletValueFormatter(params) {
      if (params.data !== undefined) {
        let fid = params.colDef.field;
        let formatter = this.getFormatter(fid);
        let rawFidType = formatter ? formatter.split('.')[0] : 'undefined';
        let value = params.data[fid];

        switch (rawFidType) {
          case 'int':
            return parseInt(value);
          case 'float':
            return this.$f(value, 'float.price');
          default:
            return value;
        }
      } else {
        return;
      }
    },
    createColumns(fids) {
      const base = [
        {
          groupId: 'callsGroup',
          children: [
            {
              //mandatory for all templates
              field: 'GROUP_ID',
              id: 'GROUP_ID',
              hide: true,
              rowGroup: true
            }
          ],
          headerGroupComponent: 'optionChainGroupHeaderRenderer',
          headerGroupComponentParams: {
            label: this.$t(`${i18nNamespace}.table.header.call`)
          }
        },
        {
          // mandatory for all templates

          groupId: 'strikesGroup',
          children: [
            {
              field: 'xopt._STRIKE',
              id: '_STRIKE',
              headerName: ' ',
              headerClass: 'text-center column-strike',
              cellClass: 'text-center column-strike',
              sort: 'asc',
              sortable: false,
              valueGetter: params => {
                let fid = params.colDef.field;
                let formatter = this.getFormatter(fid);
                return this.$f(params.value, formatter);
              }
            }
          ],
          headerGroupComponent: 'optionChainGroupHeaderRenderer',
          headerGroupComponentParams: {
            label: this.$t(`${i18nNamespace}.table.header.strike`)
          }
        },
        {
          groupId: 'putsGroup',
          children: [],
          headerGroupComponent: 'optionChainGroupHeaderRenderer',
          headerGroupComponentParams: {
            label: this.$t(`${i18nNamespace}.table.header.put`)
          }
        }
      ];

      fids['C'].forEach(fid => {
        // remove possible namespace from fid
        const realFid = fid.split('.').length > 1 ? fid.split('.')[1] : fid;

        // Create & add columns
        let CALLCell = {
          field: `C${realFid.charAt(0) === '_' ? realFid : '_' + realFid}`,
          id: `C${realFid.charAt(0) === '_' ? realFid : '_' + realFid}`,
          headerName: this.i18n(`templates.fids.${realFid}`),
          headerClass: 'text-center',
          sortable: false,
          headerComponent: 'optionChainHeaderRenderer',
          headerComponentParams: {
            srLabel: this.$t(`${i18nNamespace}.table.header.call`)
          },
          valueFormatter: params => {
            let fid = params.colDef.field;
            let formatter = this.getFormatter(fid);
            let rawFidType = formatter ? formatter.split('.')[0] : 'undefined';

            if (rawFidType === 'float' && Number(params.value) === 0.0)
              return this.$f(parseFloat(params.value), 'float.price');
            return this.$f(params.value, formatter);
          }
        };

        // If current column is should be a flashing one -> add cellRendererFramework prop
        if (this.tickFashCells.includes(realFid)) {
          if (['C_BID', 'C_ASK'].includes(CALLCell.field)) {
            CALLCell.cellRenderer = JSTickFlashRenderer;
            CALLCell.cellRendererParams = () => {
              return { state: this.$store.state, ariaText: this.$t('white-label.globalLabels.buttonAria') };
            };
          } else {
            CALLCell.cellRendererFramework = 'TickFlashRenderer';
          }
        }

        // If current column needs a css classRule -> add cellClassRules prop
        if (this.classRulesCells[realFid]) {
          CALLCell.cellClassRules = this.classRulesCells[realFid];
        }

        if (realFid === 'MARK') {
          CALLCell.valueFormatter = params => {
            if (!params.data) return '';
            const bid = params.data['C_BID'];
            const ask = params.data['C_ASK'];

            if (isNaN(bid) || isNaN(ask)) return '-';
            return this.$f((Number(bid) + Number(ask)) / 2, 'float.price');
          };
        }

        if (realFid === '_THEO_PRC') {
          CALLCell.cellClassRules = {
            'column-calculation-active': () => {
              return this.streamingActiveValue;
            },
            'row-indicator': params => {
              if (!this.streamingActiveValue) {
                if (+params.data['C_STRIKE'] < +this.underlyingData['q._TRDPRC_1']) {
                  return true;
                }
              }
              return false;
            }
          };
        }
        if (realFid === '_BID' || realFid === '_ASK') CALLCell.type = ['actionable'];
        base[0].children.push(CALLCell);
      });

      fids['P'].forEach(fid => {
        // remove possible namespace from fid
        const realFid = fid.split('.').length > 1 ? fid.split('.')[1] : fid;

        let PUTCell = {
          field: `P${realFid.charAt(0) === '_' ? realFid : '_' + realFid}`,
          id: `P${realFid.charAt(0) === '_' ? realFid : '_' + realFid}`,
          headerName: this.i18n(`templates.fids.${realFid}`),
          headerClass: 'text-center',
          sortable: false,
          headerComponent: 'optionChainHeaderRenderer',
          headerComponentParams: {
            srLabel: this.$t(`${i18nNamespace}.table.header.put`)
          },
          valueFormatter: params => {
            let fid = params.colDef.field;
            let formatter = this.getFormatter(fid);
            let rawFidType = formatter ? formatter.split('.')[0] : 'undefined';
            if (rawFidType === 'float' && Number(params.value) === 0.0)
              return this.$f(parseFloat(params.value), 'float.price');
            return this.$f(params.value, formatter);
          }
        };

        // If current column is should be a flashing one -> add cellRendererFramework prop
        if (this.tickFashCells.includes(realFid)) {
          if (['P_BID', 'P_ASK'].includes(PUTCell.field)) {
            PUTCell.cellRenderer = JSTickFlashRenderer;
            PUTCell.cellRendererParams = () => {
              return { state: this.$store.state, ariaText: this.$t('white-label.globalLabels.buttonAria') };
            };
          } else {
            PUTCell.cellRendererFramework = 'TickFlashRenderer';
          }
        }

        // If current column needs a css classRule -> add cellClassRules prop
        if (this.classRulesCells[realFid]) {
          PUTCell.cellClassRules = this.classRulesCells[realFid];
        }

        if (realFid === 'MARK') {
          PUTCell.valueFormatter = params => {
            if (!params.data) return '';
            const bid = params.data['P_BID'];
            const ask = params.data['P_ASK'];

            if (isNaN(bid) || isNaN(ask)) return '-';
            return this.$f((Number(bid) + Number(ask)) / 2, 'float.price');
          };
        }

        if (realFid === '_THEO_PRC') {
          PUTCell.cellClassRules = {
            'column-calculation-active': () => {
              return this.streamingActiveValue;
            },
            'row-indicator': params => {
              if (!this.streamingActiveValue) {
                if (+params.data['P_STRIKE'] >= +this.underlyingData['q._TRDPRC_1']) {
                  return true;
                }
              }
              return false;
            }
          };
        }
        if (realFid === '_BID' || realFid === '_ASK') PUTCell.type = ['actionable'];
        base[2].children.push(PUTCell);
      });

      return base;
    },
    onCellKeydown(params) {
      const event = params.event;
      const key = event.key;
      let rowIndex = params.rowIndex;
      let rowNode = params.node;

      if (
        ['Enter', ' '].includes(key) &&
        params.column &&
        ['C_BID', 'C_ASK', 'P_BID', 'P_ASK'].includes(params.column.colId)
      )
        this.onCellClicked(params);
      else if (['Tab', 'ArrowUp', 'ArrowDown'].includes(key)) {
        const isBackwards = (key === 'Tab' && event.shiftKey) || key === 'ArrowUp';
        if (!rowNode.group) {
          if (
            key === 'Tab' &&
            !(rowNode.childIndex === 0 && isBackwards && params.column.colId === 'C_BID') &&
            !(rowNode.lastChild && !isBackwards && params.column.colId === 'P_ASK')
          )
            return; // normal processing
        }

        if (rowIndex === 0 && isBackwards) {
          params.api.clearFocusedCell();
          const headerRows = this.$el.querySelectorAll('.ag-header-container > .ag-header-row');
          headerRows
            .item(1)
            .querySelector('.ag-header-cell')
            .focus();
        } else {
          const nextRowIndex = rowIndex + (isBackwards ? -1 : 1);
          const nextRowNode = params.api.getDisplayedRowAtIndex(nextRowIndex);
          if (nextRowNode) {
            let colId = 'GROUP_ID';
            if (!nextRowNode.group) {
              if (!rowNode.group) return; // normal processing
              if (key === 'Tab') colId = isBackwards ? 'P_ASK' : 'C_BID';
              else {
                const columns = params.columnApi.getAllDisplayedColumns();
                colId = columns[0].colId;
              }
            }
            params.api.setFocusedCell(nextRowIndex, colId);
          }
        }
      }
    },
    // Track whether and which row has been opened/closed
    onRowGroupOpened(event) {
      const index = this.rowGroupsOpenValue.findIndex(r => r === event.node.key);

      if (event.expanded) {
        if (index < 0) this.$store.commit(`widget/${this.wid}/ADD_ROWGROUP`, event.node.key);
        else return;
      } else {
        if (index < 0) return;
        else this.$store.commit(`widget/${this.wid}/REMOVE_ROWGROUP`, index);
      }
      this.focusedGroupIndex = event.node.rowIndex;
      this.adjustOptionsStreaming();
    },
    expandRowGroups() {
      this.gridOptions.api.forEachNode(node => {
        if (this.rowGroupsOpenValue.includes(node.key)) {
          node.expanded = true;
        }
      });
      this.gridOptions.api.onGroupExpandedOrCollapsed();
      this.gridOptions.api.setFocusedCell(this.focusedGroupIndex, 'GROUP_ID');
    },
    validateRowGroups() {
      this.rowGroupsOpenValue.forEach((rowGroup, index) => {
        if (!this.groupModel[rowGroup]) {
          this.$store.commit(`widget/${this.wid}/REMOVE_ROWGROUP`, index);
        }
      });

      if (this.rowGroupsOpenValue.length === 0) {
        if (this.allExpiries.length > 0) {
          this.$store.commit(`widget/${this.wid}/ADD_ROWGROUP`, this.allExpiries[0].id);
        }
      }
    },
    setTableRowsHeight() {
      this.gridOptions.api.resetRowHeights();
      this.gridOptions.api.setHeaderHeight(this.agGridRowHeight);
    },
    updateLink(ric) {
      this.sendLinkUpdate(ric);
    },
    onCellClicked(event) {
      const isValidField = event.value !== undefined && !isNaN(event.value) && event.value !== '-';

      const isBidAskField =
        event.colDef.field === 'C_BID' ||
        event.colDef.field === 'C_ASK' ||
        event.colDef.field === 'P_BID' ||
        event.colDef.field === 'P_ASK';

      const isLinkmodeField = !isBidAskField && event.colDef.field !== 'xopt._STRIKE';

      if (isValidField) {
        if (isBidAskField) {
          const type_cp = event.colDef.field.substring(0, 1);
          const ric = event.data[`${type_cp}_RIC`];

          if (this.orderTypeValue === 'slo') {
            const action =
              (this.buySellAtType === 'buyAtAsk' &&
                (event.colDef.field === 'C_ASK' || event.colDef.field === 'P_ASK')) ||
              (this.buySellAtType === 'buyAtBid' && (event.colDef.field === 'C_BID' || event.colDef.field === 'P_BID'))
                ? 'BUY'
                : 'SELL';

            this.singleLegOrderClick('option', action, ric, event.value);
          } else {
            this.multiLegOrderClick('OPT', ric, event.colDef.field.substring(1, 5));
          }
        } else if (isLinkmodeField) {
          const optionRIC = event.data[`${event.colDef.field[0]}_RIC`];
          this.updateLink(optionRIC);
        }
      }
    },
    // Action if cell click happened while single-leg-order is active
    singleLegOrderClick(ticketType, action, ric = null, limit = null) {
      this.$widget.openOrderEntry({
        orderMode: { action: action, modifier: limit === null ? '' : 'limit', limit },
        legs: {
          firstOrder: {
            symbol: { ric: ric || this.ric },
            underlying: { ric: this.ric },
            ticketType: ticketType
          }
        }
      });
    },
    // Action if cell click happened while multi-leg-order is active
    multiLegOrderClick(type, ric, clickAction) {
      if (type === 'OPT') {
        const option = this.options[ric];

        // Determine if click/action is a Buy/Sell based on current buySellAtType setting in application settings for options
        const action =
          (this.buySellAtType === 'buyAtAsk' && clickAction === '_ASK') ||
          (this.buySellAtType === 'buyAtBid' && clickAction === '_BID')
            ? getLegAction('BTO')
            : getLegAction('STO');

        const quantity = this.$store.state.applicationSettings.orders.settings.options.defaultOrderQuantity;
        const optionLeg = new OptionLeg(action, quantity, option);

        this.addOptionToLegs(optionLeg);
      } else {
        const action = clickAction === '_ASK' ? getLegAction('B') : getLegAction('S');
        const quantity = this.$store.state.applicationSettings.orders.settings.options.defaultOrderQuantity * 100;
        const stockLeg = new StockLeg(action, quantity, this.underlyingData);

        this.addStockToLegs(stockLeg);
      }
    },

    // Option-Chain Graph
    onUpdateGraphView(graphView) {
      this.$store.commit(`widget/${this.wid}/GRAPH_VIEW_CHANGED`, graphView);
    },
    onUpdateExpiries(newTags) {
      newTags = newTags.sort(function compare(a, b) {
        return new Date(a.text) - new Date(b.text);
      });

      const oldTags = this.expiriesValue;
      if (newTags.length < oldTags.length) {
        // -> delete happened
        // -> delete series in option charts
        const newTagsTemp = newTags.map(tags => tags.id);
        const deletedSeries = this._.difference(oldTags, newTagsTemp)[0];

        this.$refs.chartTopC.removeSeriesFromChart(deletedSeries);
        this.$refs.chartTopP.removeSeriesFromChart(deletedSeries);
        this.$refs.chartBottomC.removeSeriesFromChart(deletedSeries);
        this.$refs.chartBottomP.removeSeriesFromChart(deletedSeries);
      }

      const expiries = newTags.map(tags => tags.id);

      this.$store.commit(`widget/${this.wid}/EXPIRIES_CHANGED`, expiries);

      this.adjustOptionsStreaming();
    },
    validateExpiries() {
      const validatedExpiries = [];
      this.expiriesValue.forEach(expiryID => {
        if (this.groupModel[expiryID]) {
          validatedExpiries.push(expiryID);
        }
      });

      if (validatedExpiries.length < this.expiriesValue.length) {
        this.$store.commit(`widget/${this.wid}/EXPIRIES_CHANGED`, validatedExpiries);
      }

      // If there are no expiries in state anymore due to a new RIC has been entered
      // -> create new initial default expiries set
      if (this.expiriesValue.length === 0) {
        const defaultExpiries = this.allExpiries
          .filter((e, i) => {
            if (i < this.defaultExpiriesCount) {
              return e;
            }
          })
          .map(exp => exp.id);

        this.$store.commit(`widget/${this.wid}/EXPIRIES_CHANGED`, defaultExpiries);
      }
    },
    onUpdateGraphFidTop(fid) {
      this.$store.commit(`widget/${this.wid}/GRAPH_FID_TOP_CHANGED`, fid);

      // Needed to make change from 'line' to 'scatter' being reflected
      if (fid === 'q.OPINT_1') {
        this.$refs.chartTopC.resetChart();
        this.$refs.chartTopP.resetChart();
      }

      this.updateGraphData();
    },
    onUpdateGraphFidBottom(fid) {
      this.$store.commit(`widget/${this.wid}/GRAPH_FID_BOTTOM_CHANGED`, fid);

      if (fid === 'q.OPINT_1') {
        this.$refs.chartBottomC.resetChart();
        this.$refs.chartBottomP.resetChart();
      }

      this.updateGraphData();
    },
    processExpiriesData(data) {
      const expiries = data.sort(function compare(a, b) {
        return new Date(a.split('_')[1]) - new Date(b.split('_')[1]);
      });

      return expiries.map((id, i) => {
        const expiryParts = id.split('_');
        return {
          text: `${moment(expiryParts[1]).format('DD MMM YYYY')}`,
          id: id,
          colorIndex: i
        };
      });
    },
    getColorIndexForSeries(seriesID) {
      return this.allExpiries.find(e => e.id === seriesID).colorIndex;
    },
    updateGraphData() {
      const graphData = {
        top: {
          C: {},
          P: {}
        },
        bottom: {
          C: {},
          P: {}
        }
      };

      if (this.expiriesValue.length > 0 && this.isOptionChainGraphActive()) {
        this.expiriesValue.forEach(expiryID => {
          this.groupModel[expiryID].rics.forEach(ric => {
            const type_cp = this.options[ric]['xopt._TYPE_CP'];

            if (!graphData.top[type_cp].hasOwnProperty(expiryID)) graphData.top[type_cp][expiryID] = [];
            if (!graphData.bottom[type_cp].hasOwnProperty(expiryID)) graphData.bottom[type_cp][expiryID] = [];

            graphData.top[type_cp][expiryID].push({
              x: parseFloat(this.options[ric]['xopt._STRIKE']),
              y: parseFloat(this.options[ric][this.graphFidTopValue]),
              id: expiryID,
              matIso: moment(this.options[ric]['xopt._MATURITY_ISO'], 'YYYY-MM-DD').format('DD MMM YYYY'),
              fid: this.getGraphFidText(this.graphFidTopValue),
              colorIndex: this.getColorIndexForSeries(expiryID)
            });

            graphData.bottom[type_cp][expiryID].push({
              x: parseFloat(this.options[ric]['xopt._STRIKE']),
              y: parseFloat(this.options[ric][this.graphFidBottomValue]),
              id: expiryID,
              matIso: moment(this.options[ric]['xopt._MATURITY_ISO'], 'YYYY-MM-DD').format('DD MMM YYYY'),
              fid: this.getGraphFidText(this.graphFidBottomValue),
              colorIndex: this.getColorIndexForSeries(expiryID)
            });
          });
        });
      }

      // Update reactive Objects triggering change to OptionCharts
      this.chartTopC = graphData.top.C;
      this.chartTopP = graphData.top.P;
      this.chartBottomC = graphData.bottom.C;
      this.chartBottomP = graphData.bottom.P;
    },

    // Option-Chain --  Helpers
    getAbsPrice(price) {
      return this.$f(Math.abs(price), 'float.price');
    },
    getStrikePriceRange: function(tradePrice) {
      const priceRange = {
        min: null,
        max: null
      };

      const strikePrice = Math.round(tradePrice);

      let factor = 10;

      if (strikePrice <= 50) {
        factor = 2;
      } else if (strikePrice > 50 && strikePrice <= 100) {
        factor = 3;
      } else if (strikePrice > 100 && strikePrice <= 1000) {
        factor = 5;
      } else if (strikePrice > 1000 && strikePrice <= 10000) {
        factor = 8;
      }

      priceRange.min = this.strikesValue !== 'all' ? strikePrice - this.strikesValue * factor : '';
      priceRange.max = this.strikesValue !== 'all' ? strikePrice + this.strikesValue * factor : '';

      return priceRange;
    },
    getFormatter(fid) {
      if (this.formats[fid]) {
        return this.formats[fid];
      } else if (this.optionsFids[fid]) {
        return this.optionsFids[fid].formatter;
      } else if (this.optionsStreamingFids[this.optionsStreamingFidsMapping[fid]]) {
        return this.optionsStreamingFids[this.optionsStreamingFidsMapping[fid]].formatter;
      } else if (this.greeksFids[fid]) {
        return this.greeksFids[fid].formatter;
      } else if (this.intrinsicExtrinsicFids[fid]) {
        return this.intrinsicExtrinsicFids[fid].formatter;
      } else if (this.probabilityFids[fid]) {
        return this.probabilityFids[fid].formatter;
      } else if (this.theoreticalFids[fid]) {
        return this.theoreticalFids[fid].formatter;
      } else {
        return null;
      }
    },
    isExpiryVisible(expiryType) {
      return this.expiryTypes[expiryType];
    },
    getExpiryType(expiryType) {
      expiryType = expiryType.trim();
      if (expiryType === 'W') {
        return 'weekly';
      } else if (expiryType === 'M') {
        return 'monthly';
      } else if (expiryType === 'Q') {
        return 'quarterly';
      } else if (expiryType === 'E') {
        return 'weekly';
      } else {
        return 'monthly';
      }
    },
    getExpiryTypeFromDisplayName(displayName) {
      let expiryType = 'monthly';

      if (displayName && displayName !== '-' && displayName.trim() !== '') {
        const parts = displayName.trim().split(' ');
        if (parts.length >= 2) {
          const first = parts[1].charAt(0);
          const last = parts[1].charAt(parts[1].length - 1);
          if (first === '1' || first === '2' || first === '4' || first === '5') {
            expiryType = 'weekly';
          } else if (last === 'Q') {
            expiryType = 'quarterly';
          }
        }
      }

      return expiryType;
    },
    momentTZ(date) {
      return moment(toTimezoneDate(this.timezone, date));
    },
    processRowGroupTitle(groupID) {
      const expDate = moment(groupID.split('_')[1]).format('DD MMM YYYY');
      const currentDate = moment()
        .tz('America/New_York')
        .format('DD MMM YYYY');
      const expiryDays = Math.abs(moment(currentDate).diff(moment(expDate), 'days'));
      const expiryType = capitalize(this.groupModel[groupID].expiryType);

      return `${expDate} (${expiryDays} ${
        expiryDays == 1 ? ` ${this.i18n('table.group.dayToExpiry')}` : ` ${this.i18n('table.group.daysToExpiry')}`
      }, ${this.groupModel[groupID].shares}${this.i18n('table.group.' + expiryType.toLowerCase())}${
        this.groupModel[groupID].isNonStandard ? ', Non-Standard' : ''
      })`;
    },
    getBuySellClasses(action) {
      return (this.buySellAtType === 'buyAtAsk' && action === 'ask') ||
        (this.buySellAtType === 'buyAtBid' && action === 'bid')
        ? 'column-buy'
        : 'column-sell';
    },

    // Option-Chain Graph -- Helpers
    isOptionChainGraphActive() {
      return this.viewValue === 1;
    },
    getGraphFidText(fid) {
      return this.graphFidOptions.filter(f => f.value === fid)[0].text;
    },
    resetOptionChainGraphCharts() {
      if (!this.$refs.chartBottomP) return;
      this.$refs.chartTopC.resetChart();
      this.$refs.chartTopP.resetChart();
      this.$refs.chartBottomC.resetChart();
      this.$refs.chartBottomP.resetChart();
    },
    onHeaderRowKeydown(e) {
      // focusing first cell
      if (
        (e.key === 'Tab' && !e.shiftKey) ||
        (e.key === 'ArrowDown' && e.target.closest('.ag-header-row').getAttribute('aria-rowindex') === '2')
      ) {
        this.gridOptions.api.setFocusedCell(0, 'GROUP_ID');
        e.preventDefault();
      }
    },
    navigateToNextHeaderWidget(params) {
      if (params.nextHeaderPosition.headerRowIndex === -1) return null;
      return params.nextHeaderPosition;
    },
    navigateToNextCellWidget(params) {
      const nextCell = params.nextCellPosition;
      if (nextCell && nextCell.column.colId === 'GROUP_ID') {
        if (nextCell.rowIndex < 0) return null;
        const columns = nextCell.column.columnApi.getAllDisplayedColumns();
        nextCell.column = columns[0];
      }
      return nextCell;
    },
    tabToNextHeaderWidget() {
      // there are no actionable cells in the header. keydown event handles forward tab
      return null;
    },
    onDropdownFormFocus(ev) {
      ev.target.querySelector('input').focus();
    },
    customStrikesFormatter(value) {
      if (value === '') return value;
      return value < 1 ? '1' : parseInt(value).toString();
    },
    // onAsyncTransactionsFlushed is copied from agGridCommons mixin
    onAsyncTransactionsFlushed() {
      /* enable to support widget synchronization during delayed updates
      if (this.reflushFlag.includes(this.wid)) {
        if (this.reflushFlag[0] !== this.wid) {
          this.$store.commit('widget/NO_SHARE_RESET_REFLUSH_FLAG', this.wid);
        }
      } else this.$store.commit('widget/NO_SHARE_SET_REFLUSH_FLAG', this.wid);
          */
    }
  },
  created() {
    Object.assign(this.calculationParameters, {
      stockPrice: this.stockPrice,
      impVola: this.impVola,
      targetDate: this.targetDate
    });
  },
  beforeMount() {
    // Handles sorting of row groups in ag-grid
    this.gridOptions.defaultGroupOrderComparator = (nodeA, nodeB) => {
      const a = new Date(nodeA.key.split('_')[1]);
      const b = new Date(nodeB.key.split('_')[1]);
      return a - b;
    };
    // Create ag-grid columns based on selected template
    const cols = this.createColumns(this.tableFids);
    this.columns = cols;
  },
  mounted() {
    if (this.ric) this.streamingConnect().then(this.fetch);
  },
  beforeDestroy() {
    this.streamingUnsubscribe(this.symbolSubscriptionId);
    this.streamingUnsubscribe(this.optionsSubscriptionId);
    this.$el.querySelector('.ag-header-container').removeEventListener('keydown', this.onHeaderRowKeydown);
    if (this.gridOptions) this.gridOptions = null;
    if (this.gridApi) this.gridApi = null;
  }
};
</script>
<style lang="scss">
@import '~@/styles/vendor/ag-grid';
@import '~@/styles/widgets/option-chain-widget';
</style>
